<div [class]="mixedMode() ? 'unified-tile-body h-full' : ''">
    <div class="grid grid-cols-12 gap-3 lg:gap-4 grid-flow-row-dense items-stretch">
        @for (sec of embeddedVisibleSections(); track trackSection($index, sec)) {
        <div [ngClass]="getSectionColClass(sec.id)" [style.order]="sectionOrder(sec.id)" class="group relative">
            @if (!mixedMode() && canPinToCustomDashboard(sec)) {
            <button type="button" (click)="addToCustomDashboard(sec); $event.stopPropagation()"
                [disabled]="isAddingToCustomDashboard(sec)"
                class="group/pin absolute cursor-pointer top-2 right-2 z-30 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border shadow-sm text-[11px] font-medium whitespace-nowrap opacity-0 translate-y-[-2px] group-hover:opacity-100 group-hover:translate-y-0 focus-visible:opacity-100 focus-visible:translate-y-0 transition-all duration-200 disabled:cursor-default"
                [ngClass]="isAddedToCustomDashboard(sec)
        ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-rose-50 hover:text-rose-700 hover:border-rose-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:border-emerald-800 dark:hover:bg-rose-900/30 dark:hover:text-rose-300 dark:hover:border-rose-800 !opacity-100 !translate-y-0'
        : 'bg-white/95 text-[#AD46FF] border-[#AD46FF]/30 hover:bg-violet-50 hover:border-[#AD46FF]/60 hover:shadow-md dark:bg-slate-800/95 dark:border-purple-500/40 dark:hover:bg-white/5'"
                [attr.aria-label]="isAddedToCustomDashboard(sec) ? 'Remove from My Custom Dashboard' : 'Add to My Custom Dashboard'"
                [title]="isAddedToCustomDashboard(sec) ? 'Click to remove from My Custom Dashboard' : 'Add to My Custom Dashboard'">
                @if (isAddingToCustomDashboard(sec)) {
                <svg class="animate-spin" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span>{{ isAddedToCustomDashboard(sec) ? 'Removing' : 'Adding' }}&hellip;</span>
                } @else if (isAddedToCustomDashboard(sec)) {
                <svg class="shrink-0 block group-hover/pin:hidden" xmlns="http://www.w3.org/2000/svg" width="11"
                    height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 6 9 17l-5-5" />
                </svg>
                <svg class="shrink-0 hidden group-hover/pin:block" xmlns="http://www.w3.org/2000/svg" width="11"
                    height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.8"
                    stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14" />
                </svg>
                <span
                    class="inline-block max-w-0 -ml-1.5 group-hover/pin:max-w-[220px] group-hover/pin:ml-0 focus-visible:max-w-[220px] focus-visible:ml-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out">Remove
                    from Custom Dashboard</span>
                } @else {
                <svg class="shrink-0" xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24"
                    fill="none" stroke="currentColor" stroke-width="2.8" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M5 12h14" />
                    <path d="M12 5v14" />
                </svg>
                <span
                    class="inline-block max-w-0 -ml-1.5 group-hover/pin:max-w-[200px] group-hover/pin:ml-0 focus-visible:max-w-[200px] focus-visible:ml-0 overflow-hidden whitespace-nowrap transition-all duration-300 ease-out">Pin
                    to Custom Dashboard</span>
                }
            </button>
            }

            @switch (sec.id) {

            @case (SectionId.SYSTEM_INTEL) {
            <!-- IMPORTANT POINTERS -->
            <div
                class="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50/30 dark:from-surface-0/80 dark:via-surface-0/60 dark:to-indigo-950/30 rounded-xl border border-slate-200/80 dark:border-gray-700/50 shadow-lg shadow-slate-200/30 dark:shadow-none transition-all duration-500">
                <!-- Animated background decorations -->
                <div
                    class="absolute -top-20 -left-20 w-48 h-48 bg-gradient-to-br from-blue-400/10 to-indigo-500/5 blur-[80px] rounded-full pointer-events-none">
                </div>
                <div
                    class="absolute -bottom-20 -right-20 w-48 h-48 bg-gradient-to-br from-violet-500/10 to-purple-500/5 blur-[80px] rounded-full pointer-events-none">
                </div>

                <!-- Main accordion header -->
                <button
                    class="relative z-10 w-full px-3 py-2 flex items-center justify-between group transition-all duration-500 cursor-pointer overflow-hidden"
                    (click)="toggleExpand1()">
                    <!-- Animated hover gradient -->
                    <div
                        class="absolute inset-0 bg-gradient-to-r from-blue-500/5 via-indigo-500/5 to-violet-500/5 dark:from-blue-500/10 dark:via-indigo-500/10 dark:to-violet-500/10 opacity-0 group-hover:opacity-100 transition-all duration-700 ease-out">
                    </div>

                    <div class="flex items-center gap-3 relative z-10">
                        <!-- Icon container with glow effect -->
                        <div class="relative">
                            <div
                                class="absolute inset-0 bg-blue-500/20 dark:bg-blue-400/30 rounded-lg blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                            </div>
                            <div
                                class="relative p-2 bg-gradient-to-br from-blue-500 to-indigo-600 dark:from-blue-400 dark:to-indigo-500 rounded-lg shadow-md shadow-blue-500/20 dark:shadow-blue-400/15 group-hover:shadow-blue-500/30 group-hover:scale-105 transition-all duration-500 flex items-center justify-center">
                                <app-icon-v2 name="wallet" [class]="'text-white'" [size]="16"></app-icon-v2>
                            </div>
                        </div>

                        <div class="text-left">
                            <div class="flex items-center gap-2">
                                <h2 class="text-base font-medium text-slate-900 dark:text-white tracking-tight">System
                                    Intel:
                                    Arrangement Cron</h2>
                                <span
                                    class="hidden sm:inline-flex items-center px-1.5 py-0.5 rounded-md text-[10px] font-medium tracking-wider bg-[#EFF6FF] dark:bg-blue-400/20 text-[#0C66E8] dark:text-blue-400">
                                    <span class="w-1 h-1 rounded-full bg-[#0C66E8] dark:bg-blue-400 mr-1"></span>
                                    5 Topics
                                </span>
                            </div>
                            <p class="text-xs text-slate-500 dark:text-slate-400">Essential logic, execution rules, and
                                critical behaviors</p>
                        </div>
                    </div>

                    <div
                        class="p-1.5 rounded-lg bg-slate-100/80 dark:bg-surface-2/80 border border-slate-200/50 dark:border-gray-700/50 transition-all duration-500 flex items-center justify-center">
                        <app-icon-v2 [name]="isExpanded1() ? 'chevronUp' : 'chevronDown'" [size]="16"
                            [class]="'text-slate-500 dark:text-slate-400 transition-colors duration-300'" />
                    </div>
                </button>

                @if (isExpanded1()) {
                <div
                    class="relative z-10 bg-gradient-to-b from-white/60 to-slate-50/40 dark:from-surface-0/60 dark:to-surface-2/40 backdrop-blur-xl border-t border-slate-200/50 dark:border-gray-700/50">

                    <!-- Pointer 0: Critical Requirements -->
                    <div class="group/item border-b border-slate-200/50 dark:border-gray-700/30">
                        <button (click)="togglePointer(0)"
                            class="w-full flex items-center justify-between px-3 py-2 hover:bg-gradient-to-r hover:from-red-50/50 hover:to-rose-50/30 dark:hover:from-red-950/20 dark:hover:to-rose-950/10 transition-all duration-300 cursor-pointer text-left">
                            <div class="flex items-center gap-3">
                                <div
                                    class="relative p-2 rounded-lg bg-gradient-to-br from-red-100 to-rose-100 dark:from-red-900/40 dark:to-rose-900/30 text-red-600 dark:text-red-400 group-hover/item:scale-105 group-hover/item:shadow-md group-hover/item:shadow-red-500/15 transition-all duration-300 flex items-center justify-center">
                                    <app-icon-v2 name="shieldAlert" [size]="16"></app-icon-v2>
                                    <!-- <div
                                class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full animate-ping opacity-75">
                            </div> -->
                                    <!-- <div class="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-500 rounded-full"></div> -->
                                </div>
                                <div>
                                    <h3
                                        class="text-xs font-medium text-slate-900 dark:text-white flex flex-wrap items-center gap-1.5">
                                        Critical Execution Requirements
                                        <span
                                            class="px-1.5 py-px rounded text-[10px] font-medium uppercase bg-gradient-to-r from-red-400/20 to-rose-400/20 text-red-500 dark:text-red-400">Mandatory</span>
                                    </h3>
                                    <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Without these,
                                        arrangements
                                        will <strong class="text-red-600 dark:text-red-400">NEVER</strong> execute</p>
                                </div>
                            </div>
                            <div
                                class="p-1.5 rounded-md bg-slate-100/80 dark:bg-surface-2/60 group-hover/item:bg-red-100 dark:group-hover/item:bg-red-900/30 transition-colors flex items-center justify-center">
                                <app-icon-v2 [name]="openPointerIndex() === 0 ? 'chevronUp' : 'chevronDown'"
                                    class="text-slate-400 dark:text-slate-500 group-hover/item:text-red-500 dark:group-hover/item:text-red-400 transition-colors"
                                    [size]="14" />
                            </div>
                        </button>
                        @if (openPointerIndex() === 0) {
                        <div class="px-3 pb-3 pt-1 pl-[50px]">
                            <div class="space-y-3">
                                <!-- Critical alert box -->
                                <div
                                    class="p-3 bg-gradient-to-br from-red-50 to-rose-50/50 dark:from-red-950/30 dark:to-rose-950/20 border border-red-200/60 dark:border-red-800/40 rounded-lg">
                                    <div class="flex items-center gap-1.5 mb-2">
                                        <span
                                            class="text-red-600 dark:text-red-400 font-medium text-xs flex items-center gap-1">
                                            <app-icon-v2 name="triangleAlert" [size]="14"></app-icon-v2>
                                            CRITICAL REQUIREMENTS:
                                        </span>
                                    </div>
                                    <div class="space-y-1.5">
                                        <div class="flex items-center gap-2">
                                            <span
                                                class="w-1 h-1 rounded-full bg-red-500 shadow-sm shadow-red-500/50 shrink-0"></span>
                                            <p class="text-xs text-slate-700 dark:text-slate-300"><strong
                                                    class="">Contract MUST be OVERDUE</strong>
                                                (pf_status = 3). Arrangements on ACTIVE contracts will <strong
                                                    class="text-red-500 dark:text-red-400">NEVER
                                                    execute</strong>.</p>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <span
                                                class="w-1 h-1 rounded-full bg-red-500 shadow-sm shadow-red-500/50 shrink-0"></span>
                                            <p class="text-xs text-slate-700 dark:text-slate-300"><strong
                                                    class="">Arrangement date MUST be
                                                    TODAY</strong>. Past-dated
                                                arrangements will <strong class="text-red-500 dark:text-red-400">NEVER
                                                    execute</strong>.</p>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <span
                                                class="w-1 h-1 rounded-full bg-red-500 shadow-sm shadow-red-500/50 shrink-0"></span>
                                            <p class="text-xs text-slate-700 dark:text-slate-300"><strong
                                                    class="">Contract must be past due</strong> -
                                                <code
                                                    class="px-1 py-px bg-white dark:bg-slate-300 rounded text-[10px] text-purple-500">next_payment_date</code>
                                                before today.
                                            </p>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <span
                                                class="w-1 h-1 rounded-full bg-red-500 shadow-sm shadow-red-500/50 shrink-0"></span>
                                            <p class="text-xs text-slate-700 dark:text-slate-300"><strong class="">Slot
                                                    type must match</strong> -
                                                Slot (1=Morning, 2=Afternoon, 3=Evening) must match cron execution time.
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Additional tips -->
                                <div class="space-y-1.5">
                                    <div class="flex items-center gap-2">
                                        <span
                                            class="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-sm shadow-amber-500/50 shrink-0"></span>
                                        <p class="text-xs text-slate-600 dark:text-slate-400">Executed in three time
                                            slots:
                                            <strong class="text-slate-800 dark:text-white">Morning</strong>, <strong
                                                class="text-slate-800 dark:text-white">Afternoon</strong>, <strong
                                                class="text-slate-800 dark:text-white">Evening</strong>.
                                        </p>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <span
                                            class="w-1.5 h-1.5 rounded-full bg-amber-500 shadow-sm shadow-amber-500/50 shrink-0"></span>
                                        <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                                class="text-slate-800 dark:text-white">Always verify contract
                                                status</strong>
                                            before creating arrangements.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                    </div>

                    <!-- Pointer 1: Overdue Cron Interaction -->
                    <div class="group/item border-b border-slate-200/50 dark:border-gray-700/30">
                        <button (click)="togglePointer(1)"
                            class="w-full flex items-center justify-between px-3 py-2 hover:bg-gradient-to-r hover:from-orange-50/50 hover:to-amber-50/30 dark:hover:from-orange-950/20 dark:hover:to-amber-950/10 transition-all duration-300 cursor-pointer text-left">
                            <div class="flex items-center gap-3">
                                <div
                                    class="p-2 rounded-lg bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/40 dark:to-amber-900/30 text-orange-600 dark:text-orange-400 group-hover/item:scale-105 group-hover/item:shadow-md group-hover/item:shadow-orange-500/15 transition-all duration-300 flex items-center justify-center">
                                    <app-icon-v2 name="triangleAlert" [size]="16"></app-icon-v2>
                                </div>
                                <div>
                                    <h3
                                        class="text-xs font-medium text-slate-900 dark:text-white flex flex-wrap items-center gap-1.5">
                                        Overdue Cron Interaction
                                        <span
                                            class="px-1.5 py-px rounded text-[10px] font-medium uppercase bg-orange-500/10 text-orange-500 dark:bg-orange-500/20 dark:text-orange-400">Blocking</span>
                                    </h3>
                                    <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">How arrangements
                                        affect
                                        overdue cron execution</p>
                                </div>
                            </div>
                            <div
                                class="p-1.5 rounded-md bg-slate-100/80 dark:bg-surface-2 group-hover/item:bg-orange-100 dark:group-hover/item:bg-orange-900/30 transition-colors flex items-center justify-center">
                                <app-icon-v2 [name]="openPointerIndex() === 1 ? 'chevronUp' : 'chevronDown'"
                                    class="text-slate-400 dark:text-slate-500 group-hover/item:text-orange-500 dark:group-hover/item:text-orange-400 transition-colors"
                                    [size]="14" />
                            </div>
                        </button>
                        @if (openPointerIndex() === 1) {
                        <div class="px-3 pb-3 pt-1 pl-[50px]">
                            <div class="space-y-3">
                                <!-- Critical behavior box -->
                                <div
                                    class="p-3 bg-gradient-to-br from-orange-50 to-amber-50/50 dark:from-orange-950/30 dark:to-amber-950/20 border border-orange-200/60 dark:border-orange-800/40 rounded-lg">
                                    <div class="flex items-center gap-1.5 mb-2">
                                        <span
                                            class="text-orange-600 dark:text-orange-400 font-medium text-xs flex items-center gap-1">
                                            <app-icon-v2 name="triangleAlert" [size]="14"
                                                class="text-orange-600 dark:text-orange-400"></app-icon-v2>
                                            CRITICAL BEHAVIOR:</span>
                                    </div>
                                    <div class="space-y-1.5">
                                        <div class="flex items-center gap-2">
                                            <span
                                                class="w-1.5 h-1.5 rounded-full bg-orange-500 shadow-sm shadow-orange-500/50 shrink-0"></span>
                                            <p class="text-xs text-slate-700 dark:text-slate-300">Overdue cron <strong
                                                    class="text-orange-700 dark:text-orange-400">EXCLUDES</strong>
                                                contracts
                                                with <strong>arrangement_date >= TODAY</strong>.</p>
                                        </div>
                                        <div class="flex items-center gap-2">
                                            <span
                                                class="w-1.5 h-1.5 rounded-full bg-orange-500 shadow-sm shadow-orange-500/50 shrink-0"></span>
                                            <p class="text-xs text-slate-700 dark:text-slate-300">Future-dated
                                                arrangement on
                                                OVERDUE contract = overdue cron <strong
                                                    class="text-orange-700 dark:text-orange-400">WON'T RUN</strong>
                                                until date
                                                passes.</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Additional details -->
                                <div class="space-y-1.5">
                                    <div class="flex items-center gap-2">
                                        <span class="w-1.5 h-1.5 rounded-full bg-orange-500/70 shrink-0"></span>
                                        <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                                class="text-slate-800 dark:text-white">Risk:</strong> Far-future
                                            arrangements (90+ days) can pause overdue cron indefinitely.</p>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <span class="w-1.5 h-1.5 rounded-full bg-orange-500/70 shrink-0"></span>
                                        <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                                class="text-slate-800 dark:text-white">Monitor arrangements > 30
                                                days</strong>
                                            in future - may block cron unnecessarily.</p>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <span class="w-1.5 h-1.5 rounded-full bg-orange-500/70 shrink-0"></span>
                                        <p class="text-xs text-slate-600 dark:text-slate-400">Past-dated arrangement =
                                            overdue
                                            cron runs normally.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                    </div>

                    <!-- Pointer 2: Orphaned Arrangements -->
                    <div class="group/item border-b border-slate-200/50 dark:border-gray-700/30">
                        <button (click)="togglePointer(2)"
                            class="w-full flex items-center justify-between px-3 py-2 hover:bg-gradient-to-r hover:from-yellow-50/50 hover:to-amber-50/30 dark:hover:from-yellow-950/20 dark:hover:to-amber-950/10 transition-all duration-300 cursor-pointer text-left">
                            <div class="flex items-center gap-3">
                                <div
                                    class="p-2 rounded-lg bg-gradient-to-br from-yellow-100 to-amber-100 dark:from-yellow-900/40 dark:to-amber-900/30 text-yellow-600 dark:text-yellow-400 group-hover/item:scale-105 group-hover/item:shadow-md group-hover/item:shadow-yellow-500/15 transition-all duration-300 flex items-center justify-center">
                                    <app-icon-v2 name="trash2" [size]="16"></app-icon-v2>
                                </div>
                                <div>
                                    <h3 class="text-xs font-medium text-slate-900 dark:text-white">Orphaned Arrangements
                                        &
                                        Cleanup</h3>
                                    <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Arrangements that
                                        never
                                        execute</p>
                                </div>
                            </div>
                            <div
                                class="p-1.5 rounded-md bg-slate-100/80 dark:bg-surface-2 group-hover/item:bg-yellow-100 dark:group-hover/item:bg-yellow-900/30 transition-colors flex items-center justify-center">
                                <app-icon-v2 [name]="openPointerIndex() === 2 ? 'chevronUp' : 'chevronDown'"
                                    class="text-slate-400 dark:text-slate-500 group-hover/item:text-yellow-500 dark:group-hover/item:text-yellow-400 transition-colors"
                                    [size]="14" />
                            </div>
                        </button>
                        @if (openPointerIndex() === 2) {
                        <div class="px-4 pb-4 pt-1 pl-[60px]">
                            <div class="space-y-3">
                                <!-- Critical gap box -->
                                <div
                                    class="p-3 bg-gradient-to-br from-yellow-50 to-amber-50/50 dark:from-yellow-950/30 dark:to-amber-950/20 border border-yellow-200/60 dark:border-yellow-800/40 rounded-lg">
                                    <div class="flex items-center gap-1.5 mb-1.5">
                                        <span
                                            class="text-yellow-600 dark:text-yellow-400 font-medium text-xs flex items-center gap-1">
                                            <app-icon-v2 name="triangleAlert" [size]="16"></app-icon-v2>
                                            CRITICAL GAP:</span>
                                    </div>
                                    <p class="text-xs text-slate-700 dark:text-slate-300"><strong
                                            class="text-yellow-700 dark:text-yellow-500 underline decoration-yellow-500/50">NO
                                            automatic cleanup</strong> for past-dated arrangements that were never
                                        executed.
                                    </p>
                                </div>

                                <!-- Details list -->
                                <div class="space-y-1.5">
                                    <div class="flex items-center gap-2">
                                        <span class="w-1.5 h-1.5 rounded-full bg-yellow-500/70 shrink-0"></span>
                                        <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                                class="text-slate-800 dark:text-white">Past-dated arrangements</strong>
                                            remain indefinitely, won't execute or block crons.</p>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <span class="w-1.5 h-1.5 rounded-full bg-yellow-500/70 shrink-0"></span>
                                        <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                                class="text-slate-800 dark:text-white">ACTIVE contract
                                                arrangements</strong>
                                            never execute until manually deleted or contract becomes overdue.</p>
                                    </div>
                                    <div class="flex items-center gap-2">
                                        <span class="w-1.5 h-1.5 rounded-full bg-yellow-500/70 shrink-0"></span>
                                        <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                                class="text-slate-800 dark:text-white">Regularly audit</strong> for
                                            orphaned entries with past dates or on active contracts.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                    </div>

                    <!-- Pointer 3: Execution & Types -->
                    <div class="group/item border-b border-slate-200/50 dark:border-gray-700/30">
                        <button (click)="togglePointer(3)"
                            class="w-full flex items-center justify-between px-3 py-2 hover:bg-gradient-to-r hover:from-blue-50/50 hover:to-indigo-50/30 dark:hover:from-blue-950/20 dark:hover:to-indigo-950/10 transition-all duration-300 cursor-pointer text-left">
                            <div class="flex items-center gap-3">
                                <div
                                    class="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/40 dark:to-indigo-900/30 text-blue-600 dark:text-blue-400 group-hover/item:scale-105 group-hover/item:shadow-md group-hover/item:shadow-blue-500/15 transition-all duration-300 flex items-center justify-center">
                                    <app-icon-v2 name="clock" [size]="16"></app-icon-v2>
                                </div>
                                <div>
                                    <h3 class="text-xs font-medium text-slate-900 dark:text-white">Arrangement Execution
                                        &
                                        Types</h3>
                                    <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">How arrangements
                                        execute
                                        and what happens after</p>
                                </div>
                            </div>
                            <div
                                class="p-1.5 rounded-md bg-slate-100/80 dark:bg-surface-2 group-hover/item:bg-blue-100 dark:group-hover/item:bg-blue-900/30 transition-colors flex items-center justify-center">
                                <app-icon-v2 [name]="openPointerIndex() === 3 ? 'chevronUp' : 'chevronDown'"
                                    class="text-slate-400 dark:text-slate-500 group-hover/item:text-blue-500 dark:group-hover/item:text-blue-400 transition-colors"
                                    [size]="14" />
                            </div>
                        </button>
                        @if (openPointerIndex() === 3) {
                        <div class="px-4 pb-4 pt-1 pl-[60px]">
                            <div class="space-y-1.5">
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-blue-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Recurring (Type 1):</strong> Multiple
                                        payments in loop. Any failure = entire arrangement fails.</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-blue-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Partial (Type 2):</strong> Single
                                        payment. Success/failure logged to history.</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-blue-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400">Arrangements <strong
                                            class="text-slate-800 dark:text-white">deleted</strong> after execution and
                                        moved to history table.</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-blue-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Duplicates:</strong> System keeps
                                        LATEST
                                        arrangement, moves older to history.</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-blue-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Consecutive charge
                                            protection</strong>
                                        prevents duplicate charges in quick succession.</p>
                                </div>
                            </div>
                        </div>
                        }
                    </div>

                    <!-- Pointer 4: Best Practices -->
                    <div class="group/item">
                        <button (click)="togglePointer(4)"
                            class="w-full flex items-center justify-between px-3 py-2 hover:bg-gradient-to-r hover:from-emerald-50/50 hover:to-teal-50/30 dark:hover:from-emerald-950/20 dark:hover:to-teal-950/10 transition-all duration-300 cursor-pointer text-left">
                            <div class="flex items-center gap-3">
                                <div
                                    class="p-2 rounded-lg bg-gradient-to-br from-emerald-100 to-teal-100 dark:from-emerald-900/40 dark:to-teal-900/30 text-emerald-600 dark:text-emerald-400 group-hover/item:scale-105 group-hover/item:shadow-md group-hover/item:shadow-emerald-500/15 transition-all duration-300 flex items-center justify-center">
                                    <app-icon-v2 name="circleCheck" [size]="16"></app-icon-v2>
                                </div>
                                <div>
                                    <h3
                                        class="text-xs font-medium text-slate-900 dark:text-white flex flex-wrap items-center gap-1.5">
                                        Best Practices & Precautions
                                        <span
                                            class="px-1.5 py-px rounded text-[10px] font-medium uppercase bg-emerald-500/10 text-emerald-500 dark:text-emerald-400">Recommended</span>
                                    </h3>
                                    <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Essential
                                        guidelines for
                                        admin users</p>
                                </div>
                            </div>
                            <div
                                class="p-1.5 rounded-md bg-slate-100/80 dark:bg-surface-2 group-hover/item:bg-emerald-100 dark:group-hover/item:bg-emerald-900/30 transition-colors flex items-center justify-center">
                                <app-icon-v2 [name]="openPointerIndex() === 4 ? 'chevronUp' : 'chevronDown'"
                                    class="text-slate-400 dark:text-slate-500 group-hover/item:text-emerald-500 dark:group-hover/item:text-emerald-400 transition-colors"
                                    [size]="14" />
                            </div>
                        </button>
                        @if (openPointerIndex() === 4) {
                        <div class="px-4 pb-4 pt-1 pl-[60px]">
                            <div class="space-y-1.5">
                                <div class="flex items-start gap-2">
                                    <span class="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Verify contract status</strong> -
                                        Only
                                        create on OVERDUE (pf_status = 3).</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Use "Pause Payment"</strong> instead
                                        of
                                        far-future arrangements.</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Audit regularly</strong> for orphaned
                                        entries.</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Monitor > 30 days</strong> future
                                        arrangements.</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Document</strong> in
                                        <code
                                            class="px-1 py-px bg-slate-100 dark:bg-slate-800 rounded text-[10px]">arrangement_note</code>
                                        field.
                                    </p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Review history</strong> regularly for
                                        patterns and issues.</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Set alerts</strong> for extended
                                        blocking
                                        (> 30 days).</p>
                                </div>
                                <div class="flex items-center gap-2">
                                    <span class="w-1.5 h-1.5 rounded-full bg-emerald-500/70 shrink-0"></span>
                                    <p class="text-xs text-slate-600 dark:text-slate-400"><strong
                                            class="text-slate-800 dark:text-white">Clean up</strong> past-dated
                                        arrangements
                                        to keep table clean.</p>
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                </div>
                }
            </div>
            }

            @case (SectionId.SCHEDULED_TODAY) {
            <!-- Scheduled Today Card -->
            <div
                class="relative overflow-hidden group bg-white dark:bg-surface-2 rounded-xl border border-slate-200 dark:border-gray-700/50 shadow-sm transition-all duration-300 h-full">
                <div class="relative p-4">
                    @if(isLoadingScheduledToday()) {
                    <div class="space-y-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-3">
                                <div
                                    class="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600/50 animate-pulse">
                                </div>
                                <div class="space-y-1.5">
                                    <div class="h-4 w-28 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                                    <div class="h-3 w-36 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                                </div>
                            </div>
                            <div class="text-right space-y-1">
                                <div class="h-2.5 w-20 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                                <div class="h-7 w-14 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                            </div>
                        </div>

                        <div class="space-y-2">
                            <div class="flex items-center justify-between">
                                <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                                <div class="h-2.5 w-16 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                            </div>
                            <div class="h-2 w-full bg-slate-100 dark:bg-slate-700/50 rounded-full animate-pulse"></div>
                        </div>

                        <div class="grid grid-cols-3 gap-3">
                            @for(item of [1,2,3]; track item) {
                            <div
                                class="p-3 rounded-lg bg-slate-100 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600/40 h-[72px] flex flex-col justify-between">
                                <div class="flex items-center gap-1.5">
                                    <div class="w-6 h-6 bg-slate-200 dark:bg-slate-600/50 rounded animate-pulse"></div>
                                    <div class="h-2.5 w-12 bg-slate-200 dark:bg-slate-600/50 rounded animate-pulse">
                                    </div>
                                </div>
                                <div class="h-5 w-10 bg-slate-200 dark:bg-slate-600/50 rounded animate-pulse"></div>
                            </div>
                            }
                        </div>
                    </div>
                    } @else {
                    <!-- Header Section -->
                    <div class="flex items-center justify-between mb-4">
                        <div class="flex items-center gap-3">
                            <div
                                class="relative flex items-center justify-center w-10 h-10 rounded-xl bg-orange-50 dark:bg-orange-500/10 text-orange-500">
                                <app-icon-v2 name="clock" [size]="20"></app-icon-v2>
                            </div>
                            <div>
                                <div class="flex items-center gap-2 mb-0.5">
                                    <h2 class="text-[14px] font-bold text-slate-900 dark:text-white">Scheduled Today
                                    </h2>
                                    <span
                                        class="px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider bg-red-50 dark:bg-red-500/10 text-red-500 rounded-lg border border-red-100 dark:border-red-500/20">Live</span>
                                </div>
                                <p class="text-slate-400 text-[11px] font-medium">
                                    {{ (scheduledToday()?.cycle_date ?? 'Loading...') | date:'EEE, MMM d, yyyy' }}
                                </p>
                            </div>
                        </div>
                        <button (click)="openScheduledTodayPanel()"
                            class="flex items-center justify-center w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600/50 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-all duration-300 cursor-pointer"
                            title="View Arrangements">
                            <app-icon-v2 name="arrowRight" [size]="16"></app-icon-v2>
                        </button>
                    </div>

                    <!-- Progress Bar Section -->
                    <div class="mb-4">
                        <div class="flex items-center justify-between mb-3 text-[12px] font-semibold">
                            <span class="text-slate-500 dark:text-slate-400">Volume</span>
                            <span class="text-slate-900 dark:text-white">{{ scheduledToday()?.total ?? 0 }} Total</span>
                        </div>
                        <div class="h-2 w-full bg-slate-100 dark:bg-surface-0/50 rounded-full overflow-hidden">
                            <div class="h-full bg-red-400 rounded-full transition-all duration-500" style="width: 80%">
                            </div>
                        </div>
                    </div>

                    <!-- Time Slots Grid -->
                    <div class="grid grid-cols-3 gap-3">
                        <!-- Morning Slot -->
                        <div
                            class="p-3 rounded-2xl bg-white dark:bg-surface-0/40 border border-slate-200 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] transition-all duration-300">
                            <div class="flex items-center gap-2 mb-2">
                                <app-icon-v2 name="sunrise" [size]="14" class="text-orange-400"></app-icon-v2>
                                <span class="text-[11px] font-bold text-slate-500 dark:text-slate-400">Morning</span>
                            </div>
                            <div class="text-2xl font-black text-slate-900 dark:text-white leading-none tabular-nums">
                                {{ (scheduledToday()?.total_slots?.[0]?.value) ?? 0 }}
                            </div>
                        </div>

                        <!-- Afternoon Slot -->
                        <div
                            class="p-4 rounded-2xl bg-white dark:bg-surface-0/40 border border-slate-200 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] transition-all duration-300">
                            <div class="flex items-center gap-2 mb-3">
                                <app-icon-v2 name="sun" [size]="14" class="text-purple-500"></app-icon-v2>
                                <span class="text-[11px] font-bold text-slate-500 dark:text-slate-400">Afternoon</span>
                            </div>
                            <div class="text-2xl font-black text-slate-900 dark:text-white leading-none tabular-nums">
                                {{ (scheduledToday()?.total_slots?.[1]?.value) ?? 0 }}
                            </div>
                        </div>

                        <!-- Evening Slot -->
                        <div
                            class="p-4 rounded-2xl bg-white dark:bg-surface-0/40 border border-slate-200 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] transition-all duration-300">
                            <div class="flex items-center gap-2 mb-3">
                                <app-icon-v2 name="moon" [size]="14" class="text-blue-500"></app-icon-v2>
                                <span class="text-[11px] font-bold text-slate-500 dark:text-slate-400">Evening</span>
                            </div>
                            <div class="text-2xl font-black text-slate-900 dark:text-white leading-none tabular-nums">
                                {{ (scheduledToday()?.total_slots?.[2]?.value) ?? 0 }}
                            </div>
                        </div>
                    </div>
                    }
                </div>
            </div>
            }

            @case (SectionId.RECENT_ATTEMPTS) {
            <!-- Recent Attempts Card -->
            <div
                class="relative overflow-hidden group bg-white dark:bg-surface-2 rounded-xl border border-slate-200 dark:border-gray-700/50 shadow-sm transition-all duration-300 h-full">

                <div class="relative p-4">
                    @if(isLoadingRecentAttempts()) {
                    <div class="space-y-4">
                        <div class="flex items-center justify-between">
                            <div class="flex items-center gap-3">
                                <div
                                    class="flex items-center justify-center w-10 h-10 rounded-lg bg-slate-100 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600/50 animate-pulse">
                                </div>
                                <div class="space-y-1.5">
                                    <div class="h-4 w-28 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                                    <div class="h-3 w-36 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                                </div>
                            </div>
                            <div class="text-right space-y-1">
                                <div class="h-2.5 w-20 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                                <div class="h-7 w-14 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                            </div>
                        </div>

                        <div class="space-y-2">
                            <div class="flex items-center justify-between">
                                <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                                <div class="h-2.5 w-16 bg-slate-100 dark:bg-slate-700/50 rounded animate-pulse"></div>
                            </div>
                            <div class="h-2 w-full bg-slate-100 dark:bg-slate-700/50 rounded-full animate-pulse"></div>
                        </div>

                        <div class="grid grid-cols-3 gap-3">
                            @for(item of [1,2,3]; track item) {
                            <div
                                class="p-3 rounded-lg bg-slate-100 dark:bg-slate-700/40 border border-slate-200 dark:border-slate-600/40 h-[100px] flex flex-col">
                                <div class="flex items-center gap-1.5 mb-2">
                                    <div class="w-5 h-5 bg-slate-200 dark:bg-slate-600/50 rounded animate-pulse"></div>
                                    <div class="h-2.5 w-12 bg-slate-200 dark:bg-slate-600/50 rounded animate-pulse">
                                    </div>
                                </div>
                                <div class="space-y-1.5 mt-auto">
                                    <div class="h-3 w-full bg-slate-200 dark:bg-slate-600/50 rounded animate-pulse">
                                    </div>
                                    <div class="h-3 w-full bg-slate-200 dark:bg-slate-600/50 rounded animate-pulse">
                                    </div>
                                    <div class="h-3 w-full bg-slate-200 dark:bg-slate-600/50 rounded animate-pulse">
                                    </div>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                    } @else {
                    <!-- Header Section -->
                    <div class="flex items-center justify-between mb-4">
                        <div class="flex items-center gap-3">
                            <div
                                class="relative flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-500">
                                <app-icon-v2 name="History" [size]="20"></app-icon-v2>
                            </div>
                            <div>
                                <div class="flex items-center gap-2 mb-0.5">
                                    <h2 class="text-[14px] font-bold text-slate-900 dark:text-white">Recent Attempts
                                    </h2>
                                    <span
                                        class="px-1.5 py-0.5 text-[8px] font-bold uppercase tracking-wider bg-emerald-50 dark:bg-emerald-500/10 text-emerald-500 rounded-lg border border-emerald-100 dark:border-emerald-500/20">History</span>
                                </div>
                                <p class="text-slate-400 text-[11px] font-medium">
                                    {{ (recentAttempts()?.cycle_date ?? 'Loading...') | date:'EEE, MMM d, yyyy' }}
                                </p>
                            </div>
                        </div>
                        <button (click)="openRecentAttemptsPanel()"
                            class="flex items-center justify-center w-10 h-10 rounded-xl bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600/50 text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 hover:text-slate-700 dark:hover:text-slate-200 transition-all duration-300 cursor-pointer"
                            title="View Arrangements">
                            <app-icon-v2 name="arrowRight" [size]="16"></app-icon-v2>
                        </button>
                    </div>

                    <!-- Summary Section -->
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center gap-4">
                            <div class="flex items-center gap-1.5">
                                <div class="w-2 h-2 rounded-full bg-emerald-500"></div>
                                <span class="text-[12px] font-medium text-emerald-500">Success: {{
                                    recentAttempts()?.total_success ?? 0 }}</span>
                            </div>
                            <div class="flex items-center gap-1.5">
                                <div class="w-2 h-2 rounded-full bg-rose-500"></div>
                                <span class="text-[12px] font-medium text-rose-500">Failed: {{
                                    recentAttempts()?.total_failed ?? 0 }}</span>
                            </div>
                        </div>
                        <div class="text-[13px] font-bold text-slate-900 dark:text-white">
                            {{ recentAttempts()?.total ?? 0 }} Total
                        </div>
                    </div>

                    <!-- Progress Bar -->
                    <div class="h-2 w-full bg-slate-100 dark:bg-surface-0/50 rounded-full overflow-hidden flex mb-4">
                        <div [style.width.%]="(recentAttempts()?.total_success ?? 0) / ((recentAttempts()?.total ?? 1) || 1) * 100"
                            class="h-full bg-emerald-400 transition-all duration-500">
                        </div>
                        <div [style.width.%]="(recentAttempts()?.total_failed ?? 0) / ((recentAttempts()?.total ?? 1) || 1) * 100"
                            class="h-full bg-rose-400 transition-all duration-500">
                        </div>
                    </div>

                    <!-- Time Slots Grid -->
                    <div class="grid grid-cols-3 gap-4">
                        @for(slot of recentAttempts()?.total_slots; track slot.key) {
                        <div
                            class="p-4 rounded-2xl bg-white dark:bg-surface-0/40 border border-slate-200 dark:border-gray-700 shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] transition-all duration-300">
                            <div class="relative flex flex-col h-full">
                                <!-- Slot Header -->
                                <div class="flex items-center gap-2 mb-3">
                                    <app-icon-v2
                                        [name]="slot.key === 'morning' ? 'sunrise' : slot.key === 'afternoon' ? 'sun' : 'moon'"
                                        [size]="14"
                                        [class]="slot.key === 'morning' ? 'text-orange-400' : slot.key === 'afternoon' ? 'text-purple-500' : 'text-blue-500'"></app-icon-v2>
                                    <span class="text-[11px] font-bold text-slate-500 dark:text-slate-400">{{ slot.label
                                        }}</span>
                                </div>

                                <!-- Stats Body -->
                                <div class="flex items-end justify-between">
                                    <span
                                        class="text-2xl font-black text-slate-900 dark:text-white leading-none tabular-nums">{{
                                        slot.total }}</span>
                                    <div class="flex flex-col gap-0.5">
                                        <div class="flex items-center justify-between gap-3 text-[10px] font-semibold">
                                            <span class="text-emerald-500">Success</span>
                                            <span class="text-emerald-600">{{ slot.success }}</span>
                                        </div>
                                        <div class="flex items-center justify-between gap-3 text-[10px] font-semibold">
                                            <span class="text-rose-500">Failed</span>
                                            <span class="text-rose-600">{{ slot.failed }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                    }
                </div>
            </div>
            }

            @case (SectionId.KPI_SUMMARY) {
            <!-- Header Stats Cards -->
            <!--  border border-slate-200 dark:border-slate-700 p-4 bg-white/90 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl -->
            <div
                class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 border border-slate-200 dark:border-slate-700 p-4 bg-white/90 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
                <!-- Total Arrangements Card -->
                <div
                    class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-violet-600 via-violet-500 to-indigo-600 shadow-lg shadow-violet-500/20">
                    <div class="relative p-4">
                        <div class="flex justify-between items-start mb-3">
                            <div>
                                <p class="text-white/80 text-[11px] font-semibold mb-2 tracking-wide">Total Arrangements
                                </p>
                                <p class="text-3xl font-black text-white tracking-tight mb-2">
                                    {{ (headerCards()[0].value + headerCards()[0].subValue) | number }}
                                </p>
                            </div>
                            <div
                                class="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md shadow-inner">
                                <app-icon-v2 name="Radio" [size]="22" class="text-white"></app-icon-v2>
                            </div>
                        </div>
                        <p class="text-white/70 text-[11px] font-medium flex items-center gap-1.5">
                            {{ headerCards()[0].subValue | number }} Pending Execution
                        </p>
                    </div>
                </div>

                <!-- Success Rate Card -->
                <div
                    class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#B45309] via-[#D97706] to-[#F59E0B] shadow-lg shadow-orange-500/20">
                    <div class="relative p-4">
                        <div class="flex justify-between items-start mb-4">
                            <div>
                                <p class="text-white/80 text-[11px] font-semibold mb-3 tracking-wide">Success Rate</p>
                                <p class="text-3xl font-black text-white tracking-tight mb-3">
                                    {{ headerCards()[1].value | number:'1.2-2' }}%
                                </p>
                            </div>
                            <div
                                class="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md shadow-inner">
                                <app-icon-v2 name="TrendingUp" [size]="22" class="text-white"></app-icon-v2>
                            </div>
                        </div>
                        <p class="text-white/70 text-[11px] font-medium flex items-center gap-1.5">
                            {{ headerCards()[1].subValue | number }} Successful
                        </p>
                    </div>
                </div>

                <!-- Total Recovered Card -->
                <div
                    class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#047857] via-[#059669] to-[#10B981] shadow-lg shadow-emerald-500/20">
                    <div class="relative p-4">
                        <div class="flex justify-between items-start mb-4">
                            <div>
                                <p class="text-white/80 text-[11px] font-semibold mb-3 tracking-wide">Total Recovered
                                </p>
                                <p class="text-3xl font-black text-white tracking-tight mb-3">
                                    {{ headerCards()[2].value | currency:'USD':'symbol':'1.2-2' }}
                                </p>
                            </div>
                            <div
                                class="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md shadow-inner">
                                <app-icon-v2 name="CircleDollarSign" [size]="22" class="text-white"></app-icon-v2>
                            </div>
                        </div>
                        <p class="text-white/70 text-[11px] font-medium flex items-center gap-1.5">
                            Recovery Rate: {{ headerCards()[2].subValue | number:'1.2-2' }}%
                        </p>
                    </div>
                </div>

                <!-- Avg Recovery Amount Card -->
                <div
                    class="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#2563EB] via-[#3B82F6] to-[#60A5FA] shadow-lg shadow-blue-500/20">
                    <div class="relative p-4">
                        <div class="flex justify-between items-start mb-4">
                            <div>
                                <p class="text-white/80 text-[11px] font-semibold mb-3 tracking-wide">Avg Recovery
                                    Amount</p>
                                <p class="text-3xl font-black text-white tracking-tight mb-3">
                                    {{ headerCards()[3].value | currency:'USD':'symbol':'1.2-2' }}
                                </p>
                            </div>
                            <div
                                class="flex items-center justify-center w-12 h-12 rounded-2xl bg-white/15 backdrop-blur-md shadow-inner">
                                <app-icon-v2 name="Clock" [size]="22" class="text-white"></app-icon-v2>
                            </div>
                        </div>
                        <p class="text-white/70 text-[11px] font-medium flex items-center gap-1.5">
                            Per Successful Arrangement
                        </p>
                    </div>
                </div>
            </div>
            }

            @case (SectionId.TIME_GAP_DISTRIBUTION) {
            <div
                class="bg-transparent border border-slate-200 dark:border-slate-700 p-4 bg-white/90 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
                <h3 class="text-[17px] font-bold text-slate-800 dark:text-white mb-3 ml-1">
                    Average Distribution by Time Gap
                </h3>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <!-- Total Pending -->
                    <div (click)="openAllPendingPanel()"
                        class="group cursor-pointer relative p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/25 border border-blue-100 dark:border-blue-700/50 hover:shadow-lg hover:shadow-blue-500/20 dark:hover:shadow-blue-500/25 transition-all duration-300">
                        <div class="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                            <app-icon-v2 name="eye" [size]="18" class="text-blue-500 dark:text-blue-400"></app-icon-v2>
                        </div>
                        <p class="text-[12px] font-semibold text-slate-600 dark:text-slate-400 mb-2">Total Pending</p>
                        <div class="flex flex-col gap-1">
                            <span class="text-2xl font-medium text-blue-600 dark:text-blue-400">
                                {{ gapInsights().totalPending }}
                            </span>
                            <span class="text-[11px] font-medium text-slate-400 dark:text-slate-500">
                                Arrangement Schedules
                            </span>
                        </div>
                    </div>

                    <!-- Scheduled Amount -->
                    <div
                        class="p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/25 border border-emerald-100 dark:border-emerald-700/50">
                        <p class="text-[12px] font-semibold text-slate-600 dark:text-slate-400 mb-2">Scheduled Amount
                        </p>
                        <div class="flex flex-col gap-1">
                            <span class="text-2xl font-medium text-emerald-600 dark:text-emerald-400">
                                {{ pendingByGap()?.summary?.total_scheduled_amount ?? 0 | currency }}
                            </span>
                            <span class="text-[11px] font-medium text-slate-400 dark:text-slate-500">
                                Total To Be Recovered
                            </span>
                        </div>
                    </div>

                    <!-- Far Future Alert -->
                    <div (click)="openFarFuturePanel()"
                        class="group cursor-pointer relative p-4 rounded-2xl bg-rose-50 dark:bg-rose-900/25 border border-rose-100 dark:border-rose-700/50 hover:shadow-lg hover:shadow-rose-500/20 dark:hover:shadow-rose-500/25 transition-all duration-300">
                        <div class="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
                            <app-icon-v2 name="eye" [size]="18" class="text-rose-500 dark:text-rose-400"></app-icon-v2>
                        </div>
                        <p class="text-[12px] font-semibold text-slate-600 dark:text-slate-400 mb-2">Far Future Alert
                        </p>
                        <div class="flex flex-col gap-1">
                            <span class="text-2xl font-medium text-rose-500 dark:text-rose-400">
                                {{ pendingByGap()?.summary?.far_future_alert ?? 0 }}
                            </span>
                            <span class="text-[11px] font-medium text-slate-400 dark:text-slate-500">
                                Arrangements > 90 Days
                            </span>
                        </div>
                    </div>

                    <!-- Admins -->
                    <div
                        class="p-4 rounded-2xl bg-purple-50 dark:bg-purple-900/25 border border-purple-100 dark:border-purple-700/50">
                        <p class="text-[12px] font-semibold text-slate-600 dark:text-slate-400 mb-2">Admins</p>
                        <div class="flex flex-col gap-1">
                            <span class="text-2xl font-medium text-purple-600 dark:text-purple-400">
                                {{ pendingByGapUniqueAdmins() }}
                            </span>
                            <span class="text-[11px] font-medium text-slate-400 dark:text-slate-500">
                                Active Administrators
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            }

            @case (SectionId.QUICK_ACTION) {
            <div
                class="border border-slate-200 dark:border-slate-700 p-4 bg-white/90 dark:bg-surface-1/60 backdrop-blur-sm rounded-xl">
                <h3 class="text-[17px] font-bold text-slate-800 dark:text-white mb-4">
                    Quick Actions
                </h3>

                @if(isLoadingPendingByGap()) {
                <div class="flex-1 flex items-center justify-center">
                    <div
                        class="w-8 h-8 border-4 border-slate-200 dark:border-slate-700 border-t-indigo-500 rounded-full animate-spin">
                    </div>
                </div>
                } @else {
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 flex-1 content-start">
                    <div
                        class="relative overflow-hidden rounded-[24px] bg-white dark:bg-surface-2 border border-slate-100 dark:border-gray-700/50 p-4 flex flex-col justify-between min-h-[150px] hover:shadow-xl hover:shadow-amber-500/20 dark:hover:shadow-amber-500/20 transition-all duration-300 group">
                        <!-- Large Soft Blob -->
                        <div
                            class="absolute -right-4 -top-4 w-32 h-32 bg-amber-50 rounded-[4rem] dark:bg-amber-900/10 group-hover:scale-110 transition-transform duration-500">
                        </div>

                        <div class="flex justify-between items-start mb-3">
                            <div class="border-b border-slate-100 w-[180px] pb-4">
                                <p class="text-[12px] font-medium text-slate-400 mb-1">Top Administrator</p>
                                <h4 class="text-xl font-bold text-slate-800 dark:text-slate-100 leading-tight">{{
                                    gapInsights().topAdmin?.name || 'N/A' }}</h4>
                            </div>
                            <div class="relative flex-shrink-0 z-10">
                                <div
                                    class="w-16 h-16 rounded-[22px] bg-amber-500 flex items-center justify-center shadow-lg shadow-amber-500/30">
                                    <app-icon-v2 name="users" [customClass]="'text-white'" [size]="28"></app-icon-v2>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-end justify-between mt-auto">
                            <div class="flex gap-12">
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Arrangements</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().topAdmin?.count || 0 }}</p>
                                </div>
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Total Amount</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().topAdmin?.totalAmount || 0 | currency }}</p>
                                </div>
                            </div>
                            <button (click)="openTopAdminPanel()"
                                class="text-[14px] font-semibold text-[#0C66E8] hover:underline transition-all z-10 cursor-pointer">
                                View Details
                            </button>
                        </div>
                    </div>

                    <div
                        class="relative overflow-hidden rounded-[24px] bg-white dark:bg-surface-2 border border-slate-100 dark:border-gray-700/50 p-4 flex flex-col justify-between min-h-[175px] hover:shadow-xl hover:shadow-emerald-500/20 dark:hover:shadow-emerald-500/20 transition-all duration-300 group">
                        <!-- Large Soft Blob -->
                        <div
                            class="absolute -right-4 -top-4 w-32 h-32 bg-emerald-50 rounded-[4rem] dark:bg-emerald-900/10 group-hover:scale-110 transition-transform duration-500">
                        </div>

                        <div class="flex justify-between items-start mb-3">
                            <div class="border-b border-slate-100 w-[180px] pb-4">
                                <p class="text-[12px] font-medium text-slate-400 mb-1">Peak Month</p>
                                <h4 class="text-xl font-bold text-slate-800 dark:text-slate-100 leading-tight">{{
                                    gapInsights().peakMonth?.label || 'N/A' }}</h4>
                            </div>
                            <div class="relative flex-shrink-0 z-10">
                                <div
                                    class="w-16 h-16 rounded-[22px] bg-emerald-500 flex items-center justify-center shadow-lg shadow-emerald-500/30">
                                    <app-icon-v2 name="calendar" [customClass]="'text-white'" [size]="28"></app-icon-v2>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-end justify-between mt-auto">
                            <div class="flex gap-12">
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Arrangements</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().peakMonth?.count || 0 }}</p>
                                </div>
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Total Amount</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().peakMonth?.totalAmount || 0 | currency }}</p>
                                </div>
                            </div>
                            <button (click)="openPeakMonthPanel()"
                                class="text-[14px] font-semibold text-[#0C66E8] hover:underline transition-all z-10 cursor-pointer">
                                View Details
                            </button>
                        </div>
                    </div>

                    <div
                        class="relative overflow-hidden rounded-[24px] bg-white dark:bg-surface-2 border border-slate-100 dark:border-gray-700/50 p-4 flex flex-col justify-between min-h-[175px] hover:shadow-xl hover:shadow-violet-500/20 dark:hover:shadow-black/40 transition-all duration-300 group">
                        <!-- Large Soft Blob -->
                        <div
                            class="absolute -right-4 -top-4 w-32 h-32 bg-violet-50 rounded-[4rem] dark:bg-violet-900/10 group-hover:scale-110 transition-transform duration-500">
                        </div>

                        <div class="flex justify-between items-start mb-4">
                            <div class="border-b border-slate-100 w-[180px] pb-4">
                                <p class="text-[12px] font-medium text-slate-400 mb-1">Primary Payment Type</p>
                                <h4 class="text-xl font-bold text-slate-800 dark:text-slate-100 leading-tight">{{
                                    gapInsights().paymentType?.name || 'N/A' }}</h4>
                            </div>
                            <div class="relative flex-shrink-0 z-10">
                                <div
                                    class="w-16 h-16 rounded-[22px] bg-violet-500 flex items-center justify-center shadow-lg shadow-violet-500/30">
                                    <app-icon-v2 name="creditCard" [customClass]="'text-white'"
                                        [size]="28"></app-icon-v2>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-end justify-between mt-auto">
                            <div class="flex gap-12">
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Count</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">
                                        {{ gapInsights().paymentType?.count || 0 }} ({{
                                        gapInsights().paymentType?.percentage || 0 }}%)
                                    </p>
                                </div>
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Total Amount</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().paymentType?.totalAmount || 0 | currency }}</p>
                                </div>
                            </div>
                            <button (click)="openPaymentTypePanel()"
                                class="text-[14px] font-semibold text-[#0C66E8] hover:underline transition-all z-10 cursor-pointer">
                                View Details
                            </button>
                        </div>
                    </div>

                    <div
                        class="relative overflow-hidden rounded-[24px] bg-white dark:bg-surface-2 border border-slate-100 dark:border-gray-700/50 p-4 flex flex-col justify-between min-h-[175px] hover:shadow-xl hover:shadow-rose-500/20 dark:hover:shadow-rose-500/20 transition-all duration-300 group">
                        <!-- Large Soft Blob -->
                        <div
                            class="absolute -right-4 -top-4 w-32 h-32 bg-rose-50 rounded-[4rem] dark:bg-rose-900/10 group-hover:scale-110 transition-transform duration-500">
                        </div>

                        <div class="flex justify-between items-start mb-4">
                            <div class="border-b border-slate-100 w-[180px] pb-4">
                                <p class="text-[12px] font-medium text-slate-400 mb-1">Avg Days Until Execution</p>
                                <h4 class="text-xl font-bold text-slate-800 dark:text-slate-100 leading-tight">{{
                                    gapInsights().avgDays?.value || 0 }} Days</h4>
                            </div>
                            <div class="relative flex-shrink-0 z-10">
                                <div
                                    class="w-16 h-16 rounded-[22px] bg-rose-500 flex items-center justify-center shadow-lg shadow-rose-500/30">
                                    <app-icon-v2 name="clock" [customClass]="'text-white'" [size]="28"></app-icon-v2>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-end justify-between mt-auto">
                            <div class="flex gap-12">
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Earliest</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().avgDays?.earliest || 'N/A' }}</p>
                                </div>
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Latest</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().avgDays?.latest || 'N/A' }}</p>
                                </div>
                            </div>
                            <button
                                class="text-[14px] font-semibold text-[#0C66E8] hover:underline transition-all z-10 cursor-pointer">
                                View Details
                            </button>
                        </div>
                    </div>

                    <div
                        class="relative overflow-hidden rounded-[24px] bg-white dark:bg-surface-2 border border-slate-100 dark:border-gray-700/50 p-4 flex flex-col justify-between min-h-[175px] hover:shadow-xl hover:shadow-blue-500/20 dark:hover:shadow-blue-500/20 transition-all duration-300 group">
                        <!-- Large Soft Blob -->
                        <div
                            class="absolute -right-4 -top-4 w-32 h-32 bg-blue-50 rounded-[4rem] dark:bg-blue-900/10 group-hover:scale-110 transition-transform duration-500">
                        </div>

                        <div class="flex justify-between items-start mb-4">
                            <div class="border-b border-slate-100 w-[180px] pb-4">
                                <p class="text-[12px] font-medium text-slate-400 mb-1">Top Recurring</p>
                                <h4 class="text-xl font-bold text-slate-800 dark:text-slate-100 leading-tight">{{
                                    gapInsights().recurring?.count || 0 }} Arrangements</h4>
                            </div>
                            <div class="relative flex-shrink-0 z-10">
                                <div
                                    class="w-16 h-16 rounded-[22px] bg-blue-500 flex items-center justify-center shadow-lg shadow-blue-500/30">
                                    <app-icon-v2 name="repeat" [customClass]="'text-white'" [size]="28"></app-icon-v2>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-end justify-between mt-auto">
                            <div class="flex gap-12">
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Total Amount</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().recurring?.totalAmount || 0 | currency }}</p>
                                </div>
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Percentage</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().recurring?.percentage || 0 }}%</p>
                                </div>
                            </div>
                            <button (click)="openPaymentTypePanel('Recurring')"
                                class="text-[14px] font-semibold text-[#0C66E8] hover:underline transition-all z-10 cursor-pointer">
                                View Details
                            </button>
                        </div>
                    </div>

                    <div
                        class="relative overflow-hidden rounded-[24px] bg-white dark:bg-surface-2 border border-slate-100 dark:border-gray-700/50 p-4 flex flex-col justify-between min-h-[150px] hover:shadow-xl hover:shadow-orange-500/20 dark:hover:shadow-orange-500/20 transition-all duration-300 group">
                        <!-- Large Soft Blob -->
                        <div
                            class="absolute -right-4 -top-4 w-32 h-32 bg-orange-50 rounded-[4rem] dark:bg-orange-900/10 group-hover:scale-110 transition-transform duration-500">
                        </div>

                        <div class="flex justify-between items-start mb-3">
                            <div class="border-b border-slate-100 w-[180px] pb-4">
                                <p class="text-[12px] font-medium text-slate-400 mb-1">Total Partial</p>
                                <h4 class="text-xl font-bold text-slate-800 dark:text-slate-100 leading-tight">{{
                                    gapInsights().partial?.count || 0 }} Arrangements</h4>
                            </div>
                            <div class="relative flex-shrink-0 z-10">
                                <div
                                    class="w-16 h-16 rounded-[22px] bg-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
                                    <app-icon-v2 name="copy" [customClass]="'text-white'" [size]="28"></app-icon-v2>
                                </div>
                            </div>
                        </div>
                        <div class="flex items-end justify-between mt-auto">
                            <div class="flex gap-12">
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Total Amount</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().partial?.totalAmount || 0 | currency }}</p>
                                </div>
                                <div>
                                    <p class="text-[11px] text-slate-400 font-medium mb-1">Percentage</p>
                                    <p class="text-[16px] font-bold text-slate-700 dark:text-slate-200">{{
                                        gapInsights().partial?.percentage || 0 }}%</p>
                                </div>
                            </div>
                            <button (click)="openPaymentTypePanel('Partial')"
                                class="text-[14px] font-semibold text-[#0C66E8] hover:underline transition-all z-10 cursor-pointer">
                                View Details
                            </button>
                        </div>
                    </div>
                </div>
                }
            </div>
            }

            @case (SectionId.AR_AGING_OVERVIEW) {
            <div class="flex flex-col gap-6 w-full h-full">
                <div
                    class="flex flex-col gap-6 border border-slate-200 dark:border-gray-700 p-4 rounded-xl bg-slate-50/50 dark:bg-surface-0/40">
                    <div class="w-full flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2">
                        <div>
                            <h2 class="text-xl font-bold tracking-tight text-slate-800 dark:text-slate-100">Accounts
                                Receivable Aging
                            </h2>

                            <div class="flex items-center gap-8 mt-4">
                                <div class="flex flex-col gap-1">
                                    <span class="text-[12px] font-medium text-slate-400 uppercase tracking-wider">Total
                                        Outstanding</span>
                                    <span
                                        class="text-3xl font-bold text-slate-800 dark:text-slate-100">{{pendingByGap()?.summary?.total_scheduled_amount
                                        ?? 0 | currency}}</span>
                                </div>
                                <div class="flex flex-col gap-1 border-l border-slate-100 dark:border-gray-700 pl-8">
                                    <span class="text-[12px] font-medium text-slate-400 uppercase tracking-wider">Total
                                        Items</span>
                                    <span
                                        class="text-3xl font-bold text-slate-800 dark:text-slate-100">{{pendingByGap()?.summary?.total_pending
                                        ?? 0 | number}}</span>
                                </div>
                                <div class="flex flex-col gap-1 border-l border-slate-100 dark:border-gray-700 pl-8">
                                    <span
                                        class="text-[12px] font-medium text-slate-400 uppercase tracking-wider">Critical
                                        Items
                                        (90+)</span>
                                    <span
                                        class="text-3xl font-bold text-red-500">{{pendingByGap()?.summary?.far_future_alert
                                        ?? 0 |
                                        number}}</span>
                                </div>
                            </div>
                        </div>

                        <!-- <div class="flex items-start">
                        <div
                            class="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800/50">
                            <app-icon-v2 name="trendingUp" [class]="'text-emerald-600 dark:text-emerald-400'"
                                [size]="14"></app-icon-v2>
                            <span class="text-xs font-bold text-emerald-700 dark:text-emerald-300">12% improvement</span>
                        </div>
                    </div> -->
                    </div>
                    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 w-full">
                        @for(gap of pendingByGap()?.by_gap; track gap.gap_range; let index = $index; let isLast = $last)
                        {
                        <div
                            class="bg-[#F8F9FB] dark:bg-surface-2 rounded-[2rem] p-4 flex flex-col gap-6 border border-slate-100/50 dark:border-gray-700/50 hover:shadow-xl hover:shadow-slate-100/50 dark:hover:shadow-none transition-all duration-300 group">
                            <!-- Card Header -->
                            <div class="flex items-center justify-between">
                                <div class="flex items-center gap-2.5">
                                    <div class="w-2.5 h-2.5 rounded-full" [ngClass]="isLast
                                                ? 'bg-red-500 shadow-lg shadow-red-500/20'
                                                : index === 0
                                                    ? 'bg-emerald-500 shadow-lg shadow-emerald-500/20'
                                                    : index === 1
                                                        ? 'bg-blue-500 shadow-lg shadow-blue-500/20'
                                                        : index === 2
                                                            ? 'bg-violet-500 shadow-lg shadow-violet-500/20'
                                                            : 'bg-amber-500 shadow-lg shadow-amber-500/20'"></div>
                                    <h4 class="font-bold text-lg text-slate-700 dark:text-white">
                                        {{gap?.gap_range ?? '-'}} Days
                                    </h4>
                                </div>

                                <div class="flex flex-col items-end gap-1.5 relative">
                                    <span class="text-[11px] font-bold text-slate-400">
                                        {{getGapPercentage(gap?.total_arrangements ?? 0)}}% of total
                                    </span>

                                    <div class="w-16 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                                        <div class="h-full rounded-full transition-all duration-1000" [ngClass]="isLast
                                                    ? 'bg-red-500'
                                                    : index === 0
                                                        ? 'bg-emerald-500'
                                                        : index === 1
                                                            ? 'bg-blue-500'
                                                            : index === 2
                                                                ? 'bg-violet-500'
                                                                : 'bg-amber-500'"
                                            [style.width.%]="getGapPercentage(gap?.total_arrangements ?? 0)"></div>
                                    </div>
                                    @if(isLast) {
                                    <div
                                        class="absolute top-13 right-0 whitespace-nowrap px-2.5 py-1 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 text-[11px] font-bold border border-red-100 dark:border-red-800/50">
                                        High Risk</div>
                                    }
                                </div>
                            </div>

                            <!-- Main Value -->
                            <div class="flex flex-col gap-2">
                                <p class="text-[13px] font-medium text-slate-500 dark:text-slate-400">Total Outstanding
                                </p>
                                <p class="text-3xl font-bold text-slate-800 dark:text-white tracking-tight">
                                    {{gap?.total_scheduled_amount ?? 0 | currency:'USD':'symbol':'1.0-0'}}
                                </p>
                            </div>

                            <!-- Stats Grid -->
                            <div class="grid grid-cols-2 gap-y-4">
                                <div class="flex flex-col gap-1">
                                    <p class="text-[13px] font-medium text-slate-400 dark:text-slate-500">Recurring</p>
                                    <p class="text-xl font-bold text-slate-700 dark:text-slate-200">
                                        {{gap?.recurring_count ?? 0}}
                                    </p>
                                </div>
                                <div class="flex flex-col gap-1">
                                    <p class="text-[13px] font-medium text-slate-400 dark:text-slate-500">Partial</p>
                                    <p class="text-xl font-bold text-slate-700 dark:text-slate-200">
                                        {{gap?.partial_count ?? 0 | number}}
                                    </p>
                                </div>
                                <div class="flex flex-col gap-1">
                                    <p class="text-[13px] font-medium text-slate-400 dark:text-slate-500">Avg Days</p>
                                    <p class="text-xl font-bold text-slate-700 dark:text-slate-200">
                                        {{gap?.avg_days_until ?? 0}} days
                                    </p>
                                </div>
                                <div class="flex flex-col gap-1">
                                    <p class="text-[13px] font-medium text-slate-400 dark:text-slate-500">Total Items
                                    </p>
                                    <p class="text-xl font-bold text-slate-700 dark:text-slate-200">
                                        {{gap?.total_arrangements ?? 0 | number}}
                                    </p>
                                </div>
                            </div>

                            <!-- Footer -->
                            <div
                                class="mt-auto pt-4 border-t border-slate-200 dark:border-gray-700/60 flex items-center justify-between">
                                <div class="flex flex-col gap-0.5">
                                    <p class="text-[12px] font-medium text-slate-400 dark:text-slate-500">
                                        Range: {{$safeNavigationMigration(gap?.earliest_date) | date:'MM/yyyy'}} – {{$safeNavigationMigration(gap?.latest_date) |
                                        date:'MM/yyyy'}}
                                    </p>
                                </div>
                                <button (click)="openPendingByGapPanel(gap, index)"
                                    class="px-5 py-2.5 rounded-xl text-[12px] font-medium transition-all duration-300 shadow-sm cursor-pointer"
                                    [ngClass]="isLast
                                            ? 'bg-red-500 text-white hover:bg-red-600 shadow-red-200 dark:shadow-none'
                                            : 'bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 shadow-slate-100 dark:shadow-none'"
                                    title="View Records">
                                    {{isLast ? 'Review now' : 'View details'}}
                                </button>
                            </div>
                        </div>
                        }
                    </div>
                </div>

                <!-- Arrangements by Time Slot -->
                @if(!isLoadingPendingByGap() && recordsBySlot().length > 0) {
                <div class="rounded-xl border border-slate-200 dark:border-gray-700 bg-white dark:bg-surface-0/50 p-4">
                    <div class="flex items-center justify-between mb-4">
                        <h3 class="text-base font-bold text-slate-800 dark:text-slate-100">Arrangements By Time Slots
                        </h3>
                        <button (click)="openSlotPanel()"
                            class="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm">
                            View details
                        </button>
                    </div>

                    <!-- Slots Horizontal Layout -->
                    <div class="flex items-center">
                        @for(slot of recordsBySlot(); track slot.slot; let last = $last) {
                        <div (click)="openSlotPanel(slot.slot)"
                            class="relative flex-1 group cursor-pointer transition-all duration-300 hover:opacity-80">

                            <div
                                class="absolute top-0 right-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                <app-icon-v2 name="eye" [size]="18"
                                    class="text-slate-400 dark:text-slate-500"></app-icon-v2>
                            </div>

                            <!-- Slot Header (Icon + Name) -->
                            <div class="flex items-center gap-2 mb-4">
                                <app-icon-v2
                                    [name]="slot.slot === 'Morning' ? 'sunrise' : slot.slot === 'Afternoon' ? 'sun' : 'moon'"
                                    [customClass]="slot.slot === 'Morning' 
                                ? 'text-orange-500' 
                                : slot.slot === 'Afternoon' 
                                    ? 'text-purple-500'
                                    : 'text-blue-500'" [size]="18"></app-icon-v2>
                                <span class="text-xs font-medium text-slate-500 dark:text-slate-400 capitalize">
                                    {{ slot.slot }}
                                </span>
                            </div>

                            <!-- Slot Content (Count + Amount + Badge) -->
                            <div class="flex items-baseline gap-3">
                                <span class="text-3xl font-bold text-slate-900 dark:text-white tabular-nums">
                                    {{ slot.count | number }}
                                </span>
                                <span class="text-xs font-medium text-slate-400 dark:text-slate-500">
                                    {{ slot.totalAmount | currency:'USD':'symbol':'1.0-0' }}
                                </span>
                                <span class="px-2 py-0.5 text-base font-medium rounded-lg" [ngClass]="slot.slot === 'Morning' 
                                ? 'bg-orange-50 text-orange-600 dark:bg-orange-950/30 dark:text-orange-400' 
                                : slot.slot === 'Afternoon' 
                                    ? 'bg-purple-50 text-purple-600 dark:bg-purple-950/30 dark:text-purple-400'
                                    : 'bg-blue-50 text-blue-600 dark:bg-blue-950/30 dark:text-blue-400'">
                                    {{ (slot.percentage % 1 === 0 ? (slot.percentage | number:'1.0-0') :
                                    (slot.percentage |
                                    number:'1.0-1')) }}{{ slot.slot === 'Morning' ? ' %' : '%' }}
                                </span>
                            </div>
                        </div>

                        <!-- Vertical Divider -->
                        @if(!last) {
                        <div class="w-px h-16 bg-slate-100 dark:bg-slate-800 mx-8"></div>
                        }
                        }
                    </div>
                </div>
                }
            </div>
            }

            @case (SectionId.MONTHLY_DISTRIBUTION) {
            <div
                class="space-y-6 rounded-2xl border border-slate-100 dark:border-gray-700/50 bg-white dark:bg-surface-2 overflow-hidden shadow-sm h-full">
                <div
                    class="px-6 py-5 border-b border-slate-50 dark:border-gray-700/50 flex items-center justify-between gap-4">
                    <div class="flex items-center gap-3">
                        <div class="p-2 rounded-lg bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center">
                            <app-icon-v2 name="calendar" [customClass]="'text-blue-500'" [size]="18"></app-icon-v2>
                        </div>
                        <h3 class="text-base font-bold text-slate-800 dark:text-slate-100">Arrangement Distribution</h3>
                    </div>
                    <app-toggle-btns [headerOptions]="distributionToggleConfig()" [activeValue]="chartDateUnit()"
                        (viewChanged)="onChartDateUnitChange($event)">
                    </app-toggle-btns>
                </div>

                @if(!isLoadingPendingByGap() && recordsByPaymentType().length > 0) {
                <div class="px-6 grid grid-cols-1 gap-4">
                    @for(type of recordsByPaymentType(); track type.paymentType) {
                    <div class="group relative overflow-hidden rounded-2xl bg-white dark:bg-surface-0/40 border border-slate-100 dark:border-gray-700/50 p-4 flex flex-col transition-all duration-300 hover:shadow-xl"
                        [ngClass]="type.paymentType === 'Partial'
                    ? 'hover:shadow-blue-500/20 dark:hover:shadow-blue-500/25'
                    : 'hover:shadow-violet-500/20 dark:hover:shadow-black/40'">
                        <div class="absolute inset-y-0 left-0 w-1.5 transition-colors duration-300"
                            [ngClass]="type.paymentType === 'Partial' ? 'bg-blue-500' : 'bg-violet-500'"></div>

                        <div class="flex items-center gap-3 mb-3">
                            <div class="p-2 rounded-xl flex items-center justify-center"
                                [ngClass]="type.paymentType === 'Partial' ? 'bg-blue-50 dark:bg-blue-900/20' : 'bg-violet-50 dark:bg-violet-900/20'">
                                <app-icon-v2 [name]="type.paymentType === 'Partial' ? 'zap' : 'refreshCcw'"
                                    [customClass]="type.paymentType === 'Partial' ? 'text-blue-500' : 'text-violet-500'"
                                    [size]="18"></app-icon-v2>
                            </div>
                            <span class="text-sm font-bold text-slate-800 dark:text-white">{{ type.paymentType }}
                                Distribution</span>
                        </div>

                        <div
                            class="grid grid-cols-2 sm:grid-cols-4 gap-3 divide-x divide-slate-100 dark:divide-slate-800/50">
                            <div class="flex flex-col gap-1">
                                <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Count</p>
                                <p class="text-lg font-bold text-slate-800 dark:text-slate-100 tabular-nums">{{
                                    type.count | number }}</p>
                            </div>
                            <div class="flex flex-col gap-1 pl-3">
                                <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Share</p>
                                <p class="text-lg font-bold text-slate-800 dark:text-slate-100 tabular-nums">{{
                                    type.percentage | number:'1.1-1' }}%</p>
                            </div>
                            <div class="flex flex-col gap-1 pl-3">
                                <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Total</p>
                                <p class="text-lg font-bold text-slate-800 dark:text-slate-100 tabular-nums">{{
                                    type.totalAmount | currency:'USD':'symbol':'1.0-0' }}</p>
                            </div>
                            <div class="flex flex-col gap-1 pl-3">
                                <p class="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Avg</p>
                                <p class="text-lg font-bold text-slate-800 dark:text-slate-100 tabular-nums">{{
                                    type.avgAmount | currency:'USD':'symbol':'1.2-2' }}</p>
                            </div>
                        </div>
                    </div>
                    }
                </div>
                }

                <div class="p-6 pt-0 relative">
                    @if(isLoadingPendingByGap()) {
                    <div class="flex items-center justify-center min-h-[300px]">
                        <div
                            class="w-8 h-8 border-4 border-slate-100 dark:border-gray-700 border-t-blue-500 rounded-full animate-spin">
                        </div>
                    </div>
                    } @else if(arrangementDateChartSeries().length === 0) {
                    <div
                        class="flex flex-col items-center justify-center min-h-[300px] text-slate-400 dark:text-slate-500">
                        <app-icon-v2 name="chartLine" [size]="48" class="mb-3 opacity-40"></app-icon-v2>
                        <p class="text-sm font-medium">No arrangement data available</p>
                    </div>
                    } @else {
                    <app-line-area-chart [series]="arrangementDateChartSeries()" [height]="300" [showCard]="false"
                        [curveType]="'monotoneX'" [animate]="true" [useGradientFill]="true" [dateUnit]="chartDateUnit()"
                        [xAxis]="{ show: true, showGrid: false, showAxisLine: true, fontSize: 11 }"
                        [yAxis]="{ show: true, showGrid: true, showAxisLine: false, gridStyle: 'dashed', tickCount: 5, fontSize: 11 }"
                        [legend]="{ show: false }"
                        [tooltip]="{ show: true, showHoverLine: true, valueFormat: 'number', valueFormatter: chartTooltipFormatter, showSeriesLabel: true }"
                        [margins]="{ top: 20, right: 30, bottom: 50, left: 60 }">
                    </app-line-area-chart>
                    }
                </div>
            </div>
            }

            @case (SectionId.CREATION_LIFECYCLE) {
            <div
                class="rounded-2xl border border-slate-100 dark:border-gray-700/50 bg-white dark:bg-surface-2 overflow-hidden shadow-sm h-full">
                <div
                    class="px-6 py-5 border-b border-slate-50 dark:border-gray-700/50 flex items-center justify-between gap-4">
                    <div class="flex items-center gap-3">
                        <div class="p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 flex items-center justify-center">
                            <app-icon-v2 name="clock" [customClass]="'text-purple-500'" [size]="18"></app-icon-v2>
                        </div>
                        <h3 class="text-base font-bold text-slate-800 dark:text-slate-100">Creation Date Lifecycle</h3>
                    </div>
                    <app-toggle-btns [headerOptions]="distributionToggleConfig()" [activeValue]="chartDateUnit()"
                        (viewChanged)="onChartDateUnitChange($event)">
                    </app-toggle-btns>
                </div>

                <div class="p-6 relative">
                    @if(isLoadingPendingByGap()) {
                    <div class="flex items-center justify-center min-h-[300px]">
                        <div
                            class="w-8 h-8 border-4 border-slate-100 dark:border-gray-700 border-t-purple-500 rounded-full animate-spin">
                        </div>
                    </div>
                    } @else if(creationDateChartSeries().length === 0) {
                    <div
                        class="flex flex-col items-center justify-center min-h-[300px] text-slate-400 dark:text-slate-500">
                        <app-icon-v2 name="chartLine" [size]="48" class="mb-3 opacity-40"></app-icon-v2>
                        <p class="text-sm font-medium">No creation data available</p>
                    </div>
                    } @else {
                    <app-line-area-chart [series]="creationDateChartSeries()" [height]="300" [showCard]="false"
                        [curveType]="'monotoneX'" [animate]="true" [useGradientFill]="true" [dateUnit]="chartDateUnit()"
                        [xAxis]="{ show: true, showGrid: false, showAxisLine: true, fontSize: 11 }"
                        [yAxis]="{ show: true, showGrid: true, showAxisLine: false, gridStyle: 'dashed', tickCount: 5, fontSize: 11 }"
                        [legend]="{ show: false }"
                        [tooltip]="{ show: true, showHoverLine: true, valueFormat: 'number', valueFormatter: chartTooltipFormatter, showSeriesLabel: true }"
                        [margins]="{ top: 20, right: 30, bottom: 50, left: 60 }">
                    </app-line-area-chart>
                    }
                </div>
            </div>
            }

            @case (SectionId.CONTRACT_STATUS_CHART) {
            <!-- Arrangements by Contract Status Section -->
            <div class="bg-white dark:bg-surface-0/40 rounded-xl border border-slate-200 dark:border-gray-700/70 p-4">
                <!-- Section Header -->
                <div class="flex items-center justify-between mb-4">
                    <div>
                        <h2 class="text-base font-semibold text-slate-800 dark:text-white">Arrangements by Contract
                            Status</h2>
                        <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Real-time distribution of pending
                            arrangements across core contract stages.</p>
                    </div>
                    @if(recordsByStatus().length > 0) {
                    <button (click)="openStatusPanel()"
                        class="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm">
                        View details
                    </button>
                    }
                </div>

                <!-- Content -->
                @if(isLoadingPendingByGap()) {
                <div class="flex items-center justify-center min-h-[200px]">
                    <div
                        class="w-8 h-8 border-4 border-indigo-200 dark:border-indigo-800 border-t-indigo-500 rounded-full animate-spin">
                    </div>
                </div>
                } @else if(recordsByStatus().length === 0) {
                <div class="flex flex-col items-center justify-center min-h-[200px] text-slate-400 dark:text-slate-500">
                    <app-icon-v2 name="inbox" [size]="48" class="mb-3 opacity-40"></app-icon-v2>
                    <p class="text-sm font-medium">No status data available</p>
                    <p class="text-xs mt-1">Contract status distribution will appear here</p>
                </div>
                } @else {
                <!-- Status Cards - Horizontal Layout -->
                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    @for(status of recordsByStatus().slice(0, 4); track status.status; let i = $index) {
                    <div (click)="openStatusPanel(status.status)"
                        class="group relative bg-white dark:bg-surface-2 rounded-xl border border-slate-200 dark:border-slate-700 p-4 cursor-pointer hover:shadow-md transition-all duration-200">

                        <!-- Status Icon & Name with Percentage -->
                        <div class="flex items-start justify-between mb-4">
                            <div class="flex items-center">
                                <div class="w-8 h-8 rounded-full flex items-center justify-center">
                                    <!-- [ngClass]="getStatusColor(status.status).bg" -->
                                    <app-icon-v2
                                        [name]="status.status === 'Overdue' ? 'circleAlert' : status.status === 'Completed' ? 'circleCheck' : status.status === 'Cancelled' ? 'circleX' : 'circleCheckBig'"
                                        [customClass]="getStatusColor(status.status).text" [size]="20">
                                    </app-icon-v2>
                                </div>
                                <span class="text-base font-medium" [ngClass]="getStatusColor(status.status).text">{{
                                    status.status }}</span>
                                <div
                                    class="mt-1.5 ml-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                    <app-icon-v2 name="eye" [size]="18"
                                        class="text-slate-400 dark:text-slate-500"></app-icon-v2>
                                </div>
                            </div>
                            <span class="text-2xl font-bold text-slate-900 dark:text-white">{{ status.percentage |
                                number:'1.1-1' }}%</span>
                        </div>

                        <!-- Progress Bar -->
                        <div class="h-2 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden mb-4">
                            <div class="h-full rounded-full transition-all duration-700"
                                [ngClass]="getStatusColor(status.status).fill" [style.width.%]="status.percentage">
                            </div>
                        </div>

                        <!-- Stats Row -->
                        <div class="flex items-center justify-between gap-4">
                            <div class="flex-1">
                                <p class="text-[10px] text-slate-500 dark:text-slate-400 mb-1">Count</p>
                                <p class="text-base font-bold text-slate-900 dark:text-white">{{ status.count | number
                                    }}</p>
                            </div>
                            <!-- Vertical Separator -->
                            <div class="flex-1 flex items-center gap-2">
                                <div class="w-px h-10 bg-slate-200 dark:bg-slate-600"></div>
                                <div class="text-left ml-4">
                                    <p class="text-[10px] text-slate-500 dark:text-slate-400 mb-1">Amount</p>
                                    <p class="text-base font-bold text-slate-900 dark:text-white">{{ status.totalAmount
                                        | currency:'USD':'symbol':'1.0-0' }}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                    }
                </div>

                <!-- Footer Summary -->
                <div class="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div class="flex items-center gap-6">
                        <div>
                            <p class="text-xs text-slate-500 dark:text-slate-400">Total Statuses</p>
                            <p class="text-base font-bold text-slate-900 dark:text-white">{{ recordsByStatus().length }}
                                Categories</p>
                        </div>
                        <div>
                            <p class="text-xs text-slate-500 dark:text-slate-400">Total Records</p>
                            <p class="text-base font-bold text-slate-900 dark:text-white">{{
                                $safeNavigationMigration(pendingByGap()?.summary?.total_pending) | number }} Contracts</p>
                        </div>
                    </div>

                    <!-- Legend -->
                    <div class="flex items-center gap-3 bg-slate-100 dark:bg-slate-600 p-2 rounded-2xl">
                        @for(status of recordsByStatus().slice(0, 4); track status.status) {
                        <div class="flex items-center gap-1.5">
                            <div class="w-2 h-2 rounded-full" [ngClass]="getStatusColor(status.status).fill"></div>
                            <span class="text-[10px] font-medium text-slate-500 dark:text-slate-400">{{ status.status
                                }}</span>
                        </div>
                        }
                    </div>
                </div>
                }
            </div>

            <!-- High Remaining Payments Section - Horizontal Bar System -->
            @if(!isLoadingPendingByGap() && highRemainingPayments().length > 0) {
            <div class="bg-white dark:bg-surface-0/40 rounded-xl border border-slate-200 dark:border-gray-700/70 p-4">
                <!-- Header -->
                <div class="flex items-center justify-between mb-4">
                    <div>
                        <h2 class="text-base font-semibold text-slate-800 dark:text-slate-100 flex items-center gap-2">
                            High Contract Remaining Payments
                            <span
                                class="px-2 py-0.5 text-[10px] font-bold bg-emerald-50 dark:bg-emerald-900/20 text-emerald-600 dark:text-emerald-400 rounded-md">
                                +5
                            </span>
                        </h2>
                        <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                            Sorted by remaining payments (high to low)
                        </p>
                    </div>
                    <div class="flex items-center gap-3">
                        <div class="px-3 py-1.5 rounded-lg bg-purple-50 dark:bg-purple-900/20">
                            <span class="text-xs text-purple-600 dark:text-purple-400">Total: </span>
                            <span class="text-sm font-bold text-purple-700 dark:text-purple-300">{{
                                highRemainingPayments().length }} Records</span>
                        </div>
                        @if(highRemainingPayments().length > 15) {
                        <button (click)="openHighRemainingPanel()"
                            class="px-4 py-2 text-xs font-semibold text-slate-600 dark:text-slate-300 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-700 transition-all shadow-sm cursor-pointer">
                            View All
                        </button>
                        }
                    </div>
                </div>

                <!-- Vertical Bar Chart -->
                <app-vertical-single-bar [data]="highRemainingPayments()" [maxBars]="15"
                    [showViewAllButton]="false"></app-vertical-single-bar>

                <!-- Footer -->
                <!-- @if(highRemainingPayments().length > 10) {
        <div
            class="mt-4 pt-3 border-t border-slate-200/60 dark:border-slate-700/40 flex items-center justify-center gap-2">
            <app-icon-v2 name="moreHorizontal" [customClass]="'text-slate-400'" [size]="14"></app-icon-v2>
            <p class="text-[11px] text-slate-500 dark:text-slate-400">
                and <span class="font-semibold text-slate-700 dark:text-slate-300">{{ highRemainingPayments().length -
                    10 }}</span> more records
            </p>
        </div>
        } -->
            </div>
            }

            <!-- Payment Amount Distribution Section - Parallel Layout -->
            @if(!isLoadingPendingByGap() && (highestRecurringAmounts().length > 0 || highestPartialAmounts().length >
            0)) {
            <div class="bg-white dark:bg-surface-0/40 rounded-xl border border-slate-200 dark:border-gray-700/70 p-4">
                <!-- Header with Toggle Buttons -->
                <div class="flex items-start justify-between mb-5">
                    <div class="flex-1">
                        <h2 class="text-lg font-bold text-slate-900 dark:text-white mb-1">Payment Amount Distribution
                        </h2>
                    </div>

                    <!-- Toggle Buttons and View All -->
                    <div class="flex items-center gap-3">
                        <app-toggle-btns [headerOptions]="paymentToggleConfig()" [activeValue]="selectedPaymentTab()"
                            (viewChanged)="onPaymentTabChange($event)">
                        </app-toggle-btns>
                        <!-- View All Button -->
                        <button (click)="openRecurringAnalysisPanel()"
                            class="flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/40 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 transition-all duration-200 group cursor-pointer">
                            <span class="text-sm font-medium text-slate-700 dark:text-slate-300">View All</span>
                            <app-icon-v2 name="externalLink" [customClass]="'text-slate-400 group-hover:text-slate-500'"
                                [size]="12"></app-icon-v2>
                        </button>
                    </div>
                </div>

                <!-- Two Parallel Charts - Highest and Lowest for Selected Type -->
                @if(selectedPaymentTab() === 'recurring') {
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                    <!-- Highest Recurring Amounts -->
                    @if(highestRecurringAmounts().length > 0) {
                    <div
                        class="bg-white dark:bg-surface-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                        <div class="flex items-center justify-between mb-4">
                            <div class="flex items-center gap-2">
                                <h3 class="text-sm font-bold text-slate-800 dark:text-white">Highest Per-Cycle Amounts
                                </h3>
                                <span
                                    class="px-2 py-0.5 text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded">Priority
                                    Collection</span>
                            </div>
                        </div>
                        <div class="space-y-2">
                            @for(record of highestRecurringAmounts(); track record.arrangementId; let i = $index) {
                            <div class="flex items-center gap-3">
                                <span class="w-6 text-xs font-bold text-slate-500 dark:text-slate-400">{{ i + 1
                                    }}</span>
                                <div
                                    class="flex-1 h-8 bg-slate-100 dark:bg-slate-700/40 rounded-lg overflow-hidden relative">
                                    <div class="absolute inset-y-0 left-0 rounded-lg transition-all duration-300"
                                        [style.width.%]="(record.recurringAmount / highestRecurringAmounts()[0].recurringAmount) * 100"
                                        [ngClass]="i === 0 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                                    : i < 3 ? 'bg-gradient-to-r from-emerald-300 to-emerald-400'
                                    : 'bg-gradient-to-r from-emerald-200 to-emerald-300'">
                                    </div>
                                    <div class="absolute inset-0 flex items-center justify-between px-3">
                                        <span
                                            class="text-xs font-medium text-slate-800 dark:text-white truncate max-w-[60%]">{{
                                            record.patientName }}</span>
                                        <span class="text-xs font-bold text-slate-900 dark:text-white">{{
                                            record.recurringAmount | currency:'USD':'symbol':'1.0-0' }}</span>
                                    </div>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                    }

                    <!-- Lowest Recurring Amounts -->
                    @if(lowestRecurringAmounts().length > 0) {
                    <div
                        class="bg-white dark:bg-surface-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                        <div class="flex items-center justify-between mb-4">
                            <div class="flex items-center gap-2">
                                <h3 class="text-sm font-bold text-slate-800 dark:text-white">Lowest Per-Cycle Amounts
                                </h3>
                                <span
                                    class="px-2 py-0.5 text-[10px] font-semibold bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 rounded">Small
                                    Payments</span>
                            </div>
                        </div>
                        <div class="space-y-2">
                            @for(record of lowestRecurringAmounts(); track record.arrangementId; let i = $index) {
                            <div class="flex items-center gap-3">
                                <span class="w-6 text-xs font-bold text-slate-500 dark:text-slate-400">{{ i + 1
                                    }}</span>
                                <div
                                    class="flex-1 h-8 bg-slate-100 dark:bg-slate-700/40 rounded-lg overflow-hidden relative">
                                    <div class="absolute inset-y-0 left-0 rounded-lg transition-all duration-300"
                                        [style.width.%]="(record.recurringAmount / lowestRecurringAmounts()[lowestRecurringAmounts().length - 1].recurringAmount) * 100"
                                        [ngClass]="i === 0 ? 'bg-gradient-to-r from-rose-400 to-rose-500' 
                                    : i < 3 ? 'bg-gradient-to-r from-rose-300 to-rose-400'
                                    : 'bg-gradient-to-r from-rose-200 to-rose-300'">
                                    </div>
                                    <div class="absolute inset-0 flex items-center justify-between px-3">
                                        <span
                                            class="text-xs font-medium text-slate-800 dark:text-white truncate max-w-[60%]">{{
                                            record.patientName }}</span>
                                        <span class="text-xs font-bold text-slate-900 dark:text-white">{{
                                            record.recurringAmount | currency:'USD':'symbol':'1.0-0' }}</span>
                                    </div>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                    }
                </div>
                }

                @if(selectedPaymentTab() === 'partial') {
                <div class="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-5">
                    <!-- Highest Partial Amounts -->
                    @if(highestPartialAmounts().length > 0) {
                    <div
                        class="bg-white dark:bg-surface-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                        <div class="flex items-center justify-between mb-4">
                            <div class="flex items-center gap-2">
                                <h3 class="text-sm font-bold text-slate-800 dark:text-white">Highest Scheduled Amounts
                                </h3>
                                <span
                                    class="px-2 py-0.5 text-[10px] font-semibold bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 rounded">High
                                    Value</span>
                            </div>
                        </div>
                        <div class="space-y-2">
                            @for(record of highestPartialAmounts(); track record.arrangementId; let i = $index) {
                            <div class="flex items-center gap-3">
                                <span class="w-6 text-xs font-bold text-slate-500 dark:text-slate-400">{{ i + 1
                                    }}</span>
                                <div
                                    class="flex-1 h-8 bg-slate-100 dark:bg-slate-700/40 rounded-lg overflow-hidden relative">
                                    <div class="absolute inset-y-0 left-0 rounded-lg transition-all duration-300"
                                        [style.width.%]="(record.partialAmount / highestPartialAmounts()[0].partialAmount) * 100"
                                        [ngClass]="i === 0 ? 'bg-gradient-to-r from-emerald-400 to-emerald-500' 
                                    : i < 3 ? 'bg-gradient-to-r from-emerald-300 to-emerald-400'
                                    : 'bg-gradient-to-r from-emerald-200 to-emerald-300'">
                                    </div>
                                    <div class="absolute inset-0 flex items-center justify-between px-3">
                                        <span
                                            class="text-xs font-medium text-slate-800 dark:text-white truncate max-w-[60%]">{{
                                            record.patientName }}</span>
                                        <span class="text-xs font-bold text-slate-900 dark:text-white">{{
                                            record.partialAmount | currency:'USD':'symbol':'1.0-0' }}</span>
                                    </div>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                    }

                    <!-- Lowest Partial Amounts -->
                    @if(lowestPartialAmounts().length > 0) {
                    <div
                        class="bg-white dark:bg-surface-2 rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                        <div class="flex items-center justify-between mb-4">
                            <div class="flex items-center gap-2">
                                <h3 class="text-sm font-bold text-slate-800 dark:text-white">Lowest Scheduled Amounts
                                </h3>
                                <span
                                    class="px-2 py-0.5 text-[10px] font-semibold bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400 rounded">Low
                                    Value</span>
                            </div>
                        </div>
                        <div class="space-y-2">
                            @for(record of lowestPartialAmounts(); track record.arrangementId; let i = $index) {
                            <div class="flex items-center gap-3">
                                <span class="w-6 text-xs font-bold text-slate-500 dark:text-slate-400">{{ i + 1
                                    }}</span>
                                <div
                                    class="flex-1 h-8 bg-slate-100 dark:bg-slate-700/40 rounded-lg overflow-hidden relative">
                                    <div class="absolute inset-y-0 left-0 rounded-lg transition-all duration-300"
                                        [style.width.%]="(record.partialAmount / lowestPartialAmounts()[lowestPartialAmounts().length - 1].partialAmount) * 100"
                                        [ngClass]="i === 0 ? 'bg-gradient-to-r from-rose-400 to-rose-500' 
                                    : i < 3 ? 'bg-gradient-to-r from-rose-300 to-rose-400'
                                    : 'bg-gradient-to-r from-rose-200 to-rose-300'">
                                    </div>
                                    <div class="absolute inset-0 flex items-center justify-between px-3">
                                        <span
                                            class="text-xs font-medium text-slate-800 dark:text-white truncate max-w-[60%]">{{
                                            record.patientName }}</span>
                                        <span class="text-xs font-bold text-slate-900 dark:text-white">{{
                                            record.partialAmount | currency:'USD':'symbol':'1.0-0' }}</span>
                                    </div>
                                </div>
                            </div>
                            }
                        </div>
                    </div>
                    }
                </div>
                }

                <!-- Info Text at Bottom -->
                <div class="w-full flex items-center justify-center">
                    <div
                        class="flex items-center justify-center gap-2 p-3 rounded-xs bg-gray-100 dark:bg-gray-700/80 border border-gray-100 dark:border-gray-800 max-w-max">
                        <app-icon-v2 name="info" [customClass]="'text-emerald-600 dark:text-emerald-400 shrink-0'"
                            [size]="16"></app-icon-v2>
                        <p class="text-xs text-slate-600 dark:text-slate-400">
                            <span class="font-semibold text-emerald-700 dark:text-emerald-300">Recurring</span> = Fixed
                            monthly installments charged per cycle |
                            <span class="font-semibold text-rose-700 dark:text-rose-300">Partial</span> = One-time or
                            variable payment amounts. Higher amounts may need priority attention for successful
                            collection.
                        </p>
                    </div>
                </div>
            </div>
            }
            }

            @case (SectionId.METRIC_ANOMALIES) {
            <!-- System Metric Anomalies (Duplicates and Deletions) -->
            <div class="bg-white dark:bg-surface-0/40 rounded-xl border border-slate-200 dark:border-gray-700/70 p-4">
                <!-- Section Header -->
                <div class="flex items-center justify-between mb-4">
                    <div>
                        <h2 class="text-base font-semibold text-slate-800 dark:text-white">System Metric Anomalies
                            (Duplicates and Deletions)</h2>
                        <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Overview of duplicate and deleted
                            arrangement entries across the system.</p>
                    </div>
                </div>

                <!-- Summary Stats Cards -->
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                    <!-- Total Duplicates Card -->
                    <div (click)="openDuplicatesByAdminPanel()"
                        class="group bg-white dark:bg-surface-2 rounded-xl border border-slate-200 dark:border-slate-700 p-4 cursor-pointer hover:shadow-md transition-all duration-200">
                        @if(isLoadingDuplicatesSummary()) {
                        <div
                            class="w-6 h-6 border-4 border-orange-300 dark:border-orange-700 border-t-orange-600 dark:border-t-orange-400 rounded-full animate-spin mx-auto my-auto">
                        </div>
                        } @else {
                        <div class="space-y-2">
                            <div class="flex items-center justify-between">
                                <p class="text-xs font-medium text-slate-500 dark:text-slate-400">Total Duplicates</p>
                                <app-icon-v2 name="eye" [size]="16"
                                    [customClass]="'text-slate-400 group-hover:text-blue-500 transition-colors'"></app-icon-v2>
                            </div>
                            <p class="text-3xl font-bold text-orange-600 dark:text-orange-400">
                                {{ duplicatesSummary()?.total_duplicates ?? 0 | number }}
                            </p>
                            <p class="text-xs text-slate-600 dark:text-slate-400">
                                {{ duplicatesSummary()?.contracts_with_duplicates ?? 0 | number }} Contracts Affected
                            </p>
                        </div>
                        }
                    </div>

                    <!-- Total Deletions Card -->
                    <div
                        class="bg-white dark:bg-surface-2 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                        @if(isLoadingDuplicatesSummary()) {
                        <div
                            class="w-6 h-6 border-4 border-red-300 dark:border-red-700 border-t-red-600 dark:border-t-red-400 rounded-full animate-spin mx-auto my-auto">
                        </div>
                        } @else {
                        <div class="space-y-2">
                            <p class="text-xs font-medium text-slate-500 dark:text-slate-400">Total Deletions</p>
                            <p class="text-3xl font-bold text-red-600 dark:text-red-400">
                                {{ duplicatesSummary()?.total_deletions ?? 0 | number }}
                            </p>
                            <p class="text-xs text-slate-600 dark:text-slate-400">
                                {{ duplicatesSummary()?.contracts_with_deletions ?? 0 | number }} Contracts Affected
                            </p>
                        </div>
                        }
                    </div>

                    <!-- Contracts Affected Card -->
                    <div
                        class="bg-white dark:bg-surface-2 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                        @if(isLoadingDuplicatesSummary()) {
                        <div
                            class="w-6 h-6 border-4 border-blue-300 dark:border-blue-700 border-t-blue-600 dark:border-t-blue-400 rounded-full animate-spin mx-auto my-auto">
                        </div>
                        } @else {
                        <div class="space-y-2">
                            <p class="text-xs font-medium text-slate-500 dark:text-slate-400">Contract Affected</p>
                            <p class="text-3xl font-bold text-blue-600 dark:text-blue-400">
                                {{ duplicatesSummary()?.contracts_affected ?? 0 | number }}
                            </p>
                            <p class="text-xs text-slate-600 dark:text-slate-400">
                                {{ (duplicatesSummary()?.avg_duplicates_per_contract ?? 0) | number:'1.1-1' }} Avg Per
                                Contract
                            </p>
                        </div>
                        }
                    </div>

                    <!-- Admins Involved Card -->
                    <div
                        class="bg-white dark:bg-surface-2 rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                        @if(isLoadingDuplicatesSummary()) {
                        <div
                            class="w-6 h-6 border-4 border-purple-300 dark:border-purple-700 border-t-purple-600 dark:border-t-purple-400 rounded-full animate-spin mx-auto my-auto">
                        </div>
                        } @else {
                        <div class="space-y-2">
                            <p class="text-xs font-medium text-slate-500 dark:text-slate-400">Admins Involved</p>
                            <p class="text-3xl font-bold text-purple-600 dark:text-purple-400">
                                {{ duplicatesSummary()?.admins_involved ?? 0 | number }}
                            </p>
                            <p class="text-xs text-slate-600 dark:text-slate-400">
                                {{ duplicatesSummary()?.total_null_entries ?? 0 | number }} Null Entries
                            </p>
                        </div>
                        }
                    </div>
                </div>
            </div>
            }

            @case (SectionId.ADMIN_DUPLICATES_TABLE) {
            @if(!isLoadingDuplicatesByAdmin() && duplicatesByAdmin()?.records && duplicatesByAdmin()!.records.length >
            0) {
            <div
                class="bg-white dark:bg-surface-2 rounded-xl border border-slate-200 dark:border-gray-700/70 overflow-hidden">
                <!-- Table Header -->
                <div class="px-5 py-4 border-b border-slate-200 dark:border-gray-700/50">
                    <div class="flex items-center justify-between">
                        <div>
                            <h3 class="text-sm font-semibold text-slate-800 dark:text-slate-100">Duplicates by Admin
                            </h3>
                            <p class="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">Administrators with
                                duplicate or deleted arrangement entries</p>
                        </div>
                        <button (click)="openDuplicatesByAdminPanel()"
                            class="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-50 hover:bg-slate-100 dark:bg-slate-800/40 dark:hover:bg-slate-800 border border-slate-200 dark:border-slate-700 transition-all duration-200 cursor-pointer">
                            <span class="text-xs font-medium text-slate-700 dark:text-slate-300">View All</span>
                            <app-icon-v2 name="externalLink" [customClass]="'text-slate-400'" [size]="12"></app-icon-v2>
                        </button>
                    </div>
                </div>

                <!-- Table using scroll-table component -->
                <app-scroll-table [tableData]="getDuplicatesTableData()" [headers]="duplicatesTableHeaders"
                    [showHeading]="false" [tableKey]="'duplicates-by-admin'" [showTableWithBorderAndRadius]="false">
                </app-scroll-table>

                <!-- Footer Summary -->
                @if(duplicatesByAdmin()?.stats) {
                <div
                    class="px-6 py-4 bg-gradient-to-r from-slate-50 to-slate-100/50 dark:from-slate-800/40 dark:to-slate-800/20 border-t border-slate-200 dark:border-slate-700">
                    <div class="flex flex-wrap items-center justify-between gap-6">
                        <div class="flex items-center gap-6">
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-full bg-rose-500"></div>
                                <span class="text-xs font-medium text-slate-600 dark:text-slate-400">Total
                                    Admins:</span>
                                <span class="text-sm font-bold text-slate-800 dark:text-white">{{
                                    duplicatesByAdmin()!.stats.total_admins }}</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-full bg-orange-500"></div>
                                <span class="text-xs font-medium text-slate-600 dark:text-slate-400">Total
                                    Duplicates:</span>
                                <span class="text-sm font-bold text-slate-800 dark:text-white">{{
                                    duplicatesByAdmin()!.stats.total_duplicate_entries }}</span>
                            </div>
                            <div class="flex items-center gap-2">
                                <div class="w-2 h-2 rounded-full bg-red-500"></div>
                                <span class="text-xs font-medium text-slate-600 dark:text-slate-400">Total
                                    Deletions:</span>
                                <span class="text-sm font-bold text-slate-800 dark:text-white">{{
                                    duplicatesByAdmin()!.stats.total_deletion_entries }}</span>
                            </div>
                        </div>
                        @if(duplicatesByAdmin()!.records.length > 5) {
                        <p class="text-[11px] text-slate-500 dark:text-slate-400">
                            Showing <span class="font-semibold">5</span> of <span class="font-semibold">{{
                                duplicatesByAdmin()!.records.length }}</span> admins
                        </p>
                        }
                    </div>
                </div>
                }
            </div>
            }
            }
            }
        </div>
        }
    </div>
</div>

<app-dashboard-layout-editor [open]="isLayoutEditorOpen()" [items]="editorItems()" [visibleCount]="editorVisibleCount()"
    (closed)="closeLayoutEditor()" (applied)="applyLayoutEditor()" (resetDefaults)="resetLayoutEditorToDefaults()"
    (cardDropped)="onEditorCardDrop($any($event))" (visibilityToggled)="toggleEditorCardVisibility($any($event))"
    (colSpanDecreased)="decreaseEditorCardColSpan($any($event))"
    (colSpanIncreased)="increaseEditorCardColSpan($any($event))" (autoAdjustWidth)="autoAdjustEditorWidths()" />

<!-- Transaction Logs Panel -->
<app-transaction-logs-panel [open]="isPanelOpen()" [config]="panelConfig()" (closed)="closePanel()">
</app-transaction-logs-panel>